import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { createPublicClient, createWalletClient, http, custom, parseUnits, formatUnits, decodeEventLog, isAddress } from 'viem';
import { polygon } from 'viem/chains';
import { HeaderLogo, Logo } from './Logo';
import coverageTerms from '../../legal/COVERAGE_TERMS.md?raw';
import termsOfService from '../../legal/TERMS_OF_SERVICE.md?raw';

// Contract ABI (complete for frontend)
const INSURANCE_ABI = [
  // ============ Policy Functions ============
  {
    name: 'purchasePolicy',
    type: 'function',
    inputs: [
      { name: 'coverageAmount', type: 'uint256' },
      { name: 'marketId', type: 'string' },
      { name: 'marketQuestion', type: 'string' },
      { name: 'coverageDurationDays', type: 'uint256' },
      { name: 'marketResolutionTime', type: 'uint64' },
      { name: 'userPosition', type: 'string' }  // NEW in V2
    ],
    outputs: [{ name: 'policyId', type: 'bytes32' }]
  },
  {
    name: 'submitClaim',
    type: 'function',
    inputs: [
      { name: 'policyId', type: 'bytes32' },
      { name: 'actualResolution', type: 'string' },
      { name: 'expectedResolution', type: 'string' },
      { name: 'evidenceDescription', type: 'string' },
      { name: 'useProtocolBond', type: 'bool' }
    ],
    outputs: [{ name: 'assertionId', type: 'bytes32' }]
  },
  {
    name: 'cancelPolicy',
    type: 'function',
    inputs: [{ name: 'policyId', type: 'bytes32' }],
    outputs: []
  },
  {
    name: 'expirePolicy',
    type: 'function',
    inputs: [{ name: 'policyId', type: 'bytes32' }],
    outputs: []
  },
  {
    name: 'batchExpirePolicies',
    type: 'function',
    inputs: [{ name: 'policyIds', type: 'bytes32[]' }],
    outputs: []
  },

  // ============ LP Functions ============
  {
    name: 'depositAsUnderwriter',
    type: 'function',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'withdrawAsUnderwriter',
    type: 'function',
    inputs: [{ name: 'sharesToBurn', type: 'uint256' }],
    outputs: []
  },

  // ============ Admin Functions ============
  {
    name: 'setPaused',
    type: 'function',
    inputs: [{ name: '_paused', type: 'bool' }],
    outputs: []
  },
  {
    name: 'setPremiumRate',
    type: 'function',
    inputs: [{ name: 'newRateBps', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'setMarketCap',
    type: 'function',
    inputs: [
      { name: 'marketId', type: 'string' },
      { name: 'capAmount', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'withdrawProtocolFees',
    type: 'function',
    inputs: [],
    outputs: []
  },
  {
    name: 'withdrawFromPool',
    type: 'function',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: []
  },

  // ============ View Functions ============
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'policies',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'policyId', type: 'bytes32' }],
    outputs: [
      { name: 'policyholder', type: 'address' },
      { name: 'coverageAmount', type: 'uint256' },
      { name: 'premiumPaid', type: 'uint256' },
      { name: 'marketId', type: 'string' },
      { name: 'marketQuestion', type: 'string' },
      { name: 'userPosition', type: 'string' },  // NEW in V2
      { name: 'expirationTime', type: 'uint64' },
      { name: 'marketResolutionTime', type: 'uint64' },
      { name: 'settled', type: 'bool' },
      { name: 'active', type: 'bool' }
    ]
  },
  {
    name: 'calculatePremium',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'coverageAmount', type: 'uint256' },
      { name: 'durationDays', type: 'uint256' },
      { name: 'marketId', type: 'string' },
      { name: 'marketResolutionTime', type: 'uint64' }
    ],
    outputs: [{ name: 'premium', type: 'uint256' }]
  },
  {
    name: 'calculateBasePremium',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'coverageAmount', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'calculateBondForPolicy',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'policyId', type: 'bytes32' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'calculateBondForCoverage',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'coverageAmount', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'canProtocolFundBond',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'getMinimumBond',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'getLPPosition',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'lp', type: 'address' }],
    outputs: [
      { name: 'shares', type: 'uint256' },
      { name: 'value', type: 'uint256' },
      { name: 'canWithdraw', type: 'bool' }
    ]
  },
  {
    name: 'getSharePrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: 'pricePerShare', type: 'uint256' }]
  },
  {
    name: 'getUtilizationBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'getMarketConcentrationBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'marketId', type: 'string' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'getEstimatedAPY',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: 'estimatedAPY', type: 'uint256' }]
  },

  // ============ State Variables ============
  {
    name: 'bondReserve',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'underwriterPool',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'protocolFees',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'totalPremiumsCollected',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'totalActiveCoverage',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'totalLPShares',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'policyCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'activeClaimsCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'premiumRateBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'lpShares',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'depositTimestamp',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'marketActiveCoverage',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'marketId', type: 'string' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'marketCap',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'marketId', type: 'string' }],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  },

  // ============ Constants ============
  {
    name: 'ASSERTION_LIVENESS',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint64' }]
  },
  {
    name: 'PROTOCOL_FEE_BPS',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'BOND_RESERVE_BPS',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'BOND_PERCENTAGE_BPS',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'WITHDRAWAL_COOLDOWN',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'MIN_DEPOSIT',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },

  // ============ Events ============
  {
    type: 'event',
    name: 'PolicyPurchased',
    inputs: [
      { name: 'policyId', type: 'bytes32', indexed: true },
      { name: 'policyholder', type: 'address', indexed: true },
      { name: 'coverageAmount', type: 'uint256', indexed: false },
      { name: 'premium', type: 'uint256', indexed: false },
      { name: 'marketId', type: 'string', indexed: false },
      { name: 'durationDays', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'ClaimSubmitted',
    inputs: [
      { name: 'policyId', type: 'bytes32', indexed: true },
      { name: 'assertionId', type: 'bytes32', indexed: true },
      { name: 'claimant', type: 'address', indexed: true },
      { name: 'bondAmount', type: 'uint256', indexed: false },
      { name: 'bondFundedByProtocol', type: 'bool', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'ClaimResolved',
    inputs: [
      { name: 'policyId', type: 'bytes32', indexed: true },
      { name: 'assertionId', type: 'bytes32', indexed: true },
      { name: 'approved', type: 'bool', indexed: false },
      { name: 'payout', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'PolicyExpired',
    inputs: [
      { name: 'policyId', type: 'bytes32', indexed: true },
      { name: 'coverageReleased', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'PolicyCancelled',
    inputs: [
      { name: 'policyId', type: 'bytes32', indexed: true },
      { name: 'refundAmount', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'UnderwriterDeposit',
    inputs: [
      { name: 'lp', type: 'address', indexed: true },
      { name: 'amount', type: 'uint256', indexed: false },
      { name: 'sharesIssued', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'UnderwriterWithdrawal',
    inputs: [
      { name: 'lp', type: 'address', indexed: true },
      { name: 'shares', type: 'uint256', indexed: false },
      { name: 'amount', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'Paused',
    inputs: [
      { name: 'isPaused', type: 'bool', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'PremiumRateUpdated',
    inputs: [
      { name: 'oldRate', type: 'uint256', indexed: false },
      { name: 'newRate', type: 'uint256', indexed: false }
    ]
  },
  {
    type: 'event',
    name: 'MarketCapSet',
    inputs: [
      { name: 'marketId', type: 'string', indexed: false },
      { name: 'cap', type: 'uint256', indexed: false }
    ]
  }
];

const ERC20_ABI = [
  {
    name: 'approve',
    type: 'function',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' }
    ],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }]
  }
];

// Network configurations - UPDATE THESE AFTER DEPLOYMENT
const NETWORKS = {
  polygon: {
    chain: polygon,
    name: 'Polygon Mainnet',
    usdc: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    insurance: '0xE4EFc5dEFd0BDE0A12fFb2DBDB114f46ad841863',
    explorer: 'https://polygonscan.com'
  }
};

// Local storage keys
const STORAGE_KEYS = {
  POLICIES: 'rugshield_policies',
  LAST_ACCOUNT: 'rugshield_last_account',
  DARK_MODE: 'rugshield_dark_mode',
  POLYMARKET_WALLET: 'rugshield_polymarket_wallet',
  DASHBOARD_INFO_DISMISSED: 'rugshield_dashboard_info_dismissed'
};

// Admin ABI extensions
const ADMIN_ABI = [
  ...INSURANCE_ABI,
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  },
  {
    name: 'withdrawProtocolFees',
    type: 'function',
    inputs: [],
    outputs: []
  },
  {
    name: 'withdrawFromPool',
    type: 'function',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'setPremiumRate',
    type: 'function',
    inputs: [{ name: 'newRateBps', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'cancelPolicy',
    type: 'function',
    inputs: [{ name: 'policyId', type: 'bytes32' }],
    outputs: []
  },
  {
    name: 'premiumRateBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'policyCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'activeClaimsCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'getMinimumBond',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  }
];

export default function App() {
  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
    return saved !== null ? JSON.parse(saved) : true; // Default to dark mode
  });

  // Core state
  const [network] = useState('polygon');
  const [account, setAccount] = useState(null);
  const [polymarketWalletAddress, setPolymarketWalletAddress] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.POLYMARKET_WALLET);
    return saved ?? '';
  });
  const [usdcBalance, setUsdcBalance] = useState('0');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [canProtocolFundBond, setCanProtocolFundBond] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [txProgress, setTxProgress] = useState(null);
  const [legalDoc, setLegalDoc] = useState(null);
  const [dashboardInfoDismissed, setDashboardInfoDismissed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DASHBOARD_INFO_DISMISSED);
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Admin state
  const [isOwner, setIsOwner] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [newPremiumRate, setNewPremiumRate] = useState('');
  const [cancelPolicyId, setCancelPolicyId] = useState('');
  const [withdrawPoolAmount, setWithdrawPoolAmount] = useState('');
  const [lookupPolicyId, setLookupPolicyId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  // User data
  const [userPolicies, setUserPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  // Form states
  const [coverageAmount, setCoverageAmount] = useState('1000');
  const [coverageAmountCap, setCoverageAmountCap] = useState(null);
  const [marketInput, setMarketInput] = useState('');
  const [eligibleMarkets, setEligibleMarkets] = useState([]);
  const [marketSearchStatus, setMarketSearchStatus] = useState({
    loading: false,
    error: null
  });
  const [marketId, setMarketId] = useState('');
  const [marketQuestion, setMarketQuestion] = useState('');
  const [marketOutcome, setMarketOutcome] = useState(null);
  const [marketOppositeOutcome, setMarketOppositeOutcome] = useState(null);
  const [marketLookupStatus, setMarketLookupStatus] = useState({
    loading: false,
    error: null,
    source: null,
    question: null,
    marketId: null,
    slug: null,
    conditionId: null,
    endDate: null,           // NEW
    endDateTimestamp: null,  // NEW
    isResolved: null
  });
  const [premium, setPremium] = useState('0');

  // Claim form
  const [selectedPolicyForClaim, setSelectedPolicyForClaim] = useState(null);
  const [actualResolution, setActualResolution] = useState('');
  const [expectedResolution, setExpectedResolution] = useState('');
  const [evidenceSummary, setEvidenceSummary] = useState('');
  const [evidencePolymarket, setEvidencePolymarket] = useState('');
  const [evidenceSocial, setEvidenceSocial] = useState('');
  const [evidenceNews, setEvidenceNews] = useState('');
  const [evidenceOther, setEvidenceOther] = useState('');
  const [useProtocolBondForClaim, setUseProtocolBondForClaim] = useState(true);

  // Underwriter form
  const [depositAmount, setDepositAmount] = useState('1000');
  const [withdrawShares, setWithdrawShares] = useState('');
  const [lpPosition, setLpPosition] = useState(null);
  const skipMarketLookupRef = useRef(false);

  const config = NETWORKS[network];
  const legalDocuments = {
    coverage: {
      title: 'Coverage Terms',
      content: coverageTerms
    },
    terms: {
      title: 'Terms of Service',
      content: termsOfService
    }
  };
  const learnContent = {
    flow: `## For Coverage Buyers

1. **Select Your Position**
   Connect your Polymarket wallet to see positions you hold.

2. **Purchase Coverage**
   Pay a risk-adjusted premium. Coverage extends 72 hours past market resolution.

3. **Market Resolves Incorrectly?**
   File a dispute with evidence within your coverage window.

4. **UMA Voters Review**
   Independent token holders verify your evidence (48-96 hours).

5. **Get Paid**
   Approved disputes pay out automatically to your wallet.`,
    pricing: `Premiums are calculated dynamically based on:
- **Coverage amount** — Higher coverage = higher premium
- **Time to resolution** — Markets resolving soon carry higher risk
- **Pool utilization** — Higher demand = surge pricing
- **Market concentration** — Heavy coverage on one market = higher premium

Typical premiums range from 2-8% depending on these factors.`,
    dispute: `- File within 72 hours of market resolution
- Provide verifiable evidence (links to official sources, news, statements)
- Bond required: $750 or 5% of coverage (whichever is higher)
- Bond refunded if dispute approved, forfeited if rejected`,
    notCovered: `- Markets that resolved correctly (even if you lost money)
- Disputes filed after the 72-hour window
- Claims without verifiable evidence`
  };

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DARK_MODE, JSON.stringify(darkMode));
    document.body.style.background = darkMode ? '#0a0a0f' : '#f5f5f5';
    const root = document.documentElement;
    root.style.setProperty('--select-option-bg', darkMode ? '#0f111a' : '#ffffff');
    root.style.setProperty('--select-option-text', darkMode ? '#f9fafb' : '#111827');
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.POLYMARKET_WALLET, polymarketWalletAddress);
  }, [polymarketWalletAddress]);

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const closeLegalModal = () => setLegalDoc(null);

  useEffect(() => {
    if (!legalDoc) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLegalModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [legalDoc]);

  // Generate theme-aware styles
  const theme = getTheme(darkMode);

  const publicClient = useMemo(() => (
    createPublicClient({
      chain: config.chain,
      transport: http()
    })
  ), [config.chain]);

  const poolSnapshot = useMemo(() => {
    const poolValue = stats ? parseFloat(stats.underwriterPool) : null;
    const activeCoverageValue = stats ? parseFloat(stats.activeCoverage || 0) : null;
    const totalPremiumsValue = stats ? parseFloat(stats.totalPremiums || 0) : null;
    const userValue = lpPosition ? parseFloat(lpPosition.value) : null;

    const formatUsd = (value, digits = 0) => (
      value === null || Number.isNaN(value)
        ? '---'
        : `$${value.toLocaleString(undefined, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        })}`
    );

    const utilization = poolValue && poolValue > 0 && activeCoverageValue !== null
      ? (activeCoverageValue / poolValue) * 100
      : null;
    const premiumShare = totalPremiumsValue !== null ? totalPremiumsValue * 0.5 : null;
    const apyEstimate = poolValue && poolValue > 0 && premiumShare !== null
      ? (premiumShare / poolValue) * 100
      : null;

    return {
      totalPool: formatUsd(poolValue),
      currentApy: apyEstimate !== null && Number.isFinite(apyEstimate) ? `${apyEstimate.toFixed(1)}%` : '---',
      utilization: utilization !== null && Number.isFinite(utilization) ? `${utilization.toFixed(1)}%` : '---',
      userPosition: userValue !== null ? formatUsd(userValue, 2) : '---'
    };
  }, [lpPosition, stats]);

  // ============ Local Storage Helpers ============
  
  const saveToStorage = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save to storage:', e);
    }
  };

  const loadFromStorage = (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to load from storage:', e);
      return null;
    }
  };

  const addPolicyToStorage = (policy) => {
    const key = `${STORAGE_KEYS.POLICIES}_${account}_${network}`;
    const existing = loadFromStorage(key) || [];
    const updated = [...existing, policy];
    saveToStorage(key, updated);
    return updated;
  };

  const dismissDashboardInfo = useCallback(() => {
    setDashboardInfoDismissed(true);
    localStorage.setItem(STORAGE_KEYS.DASHBOARD_INFO_DISMISSED, JSON.stringify(true));
  }, []);

  const reopenDashboardInfo = useCallback(() => {
    setDashboardInfoDismissed(false);
    localStorage.removeItem(STORAGE_KEYS.DASHBOARD_INFO_DISMISSED);
  }, []);

  // ============ Wallet Connection ============
  
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Please install MetaMask or another Web3 wallet');
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      setAccount(accounts[0]);
      saveToStorage(STORAGE_KEYS.LAST_ACCOUNT, accounts[0]);

      // Switch to correct network
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${config.chain.id.toString(16)}` }]
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${config.chain.id.toString(16)}`,
              chainName: config.name,
              nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
              rpcUrls: [config.chain.rpcUrls.default.http[0]],
              blockExplorerUrls: [config.explorer]
            }]
          });
        }
      }

      setError(null);
      setActiveTab('dashboard');
    } catch (err) {
      setError('Failed to connect wallet: ' + err.message);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setUserPolicies([]);
    setUsdcBalance('0');
    localStorage.removeItem(STORAGE_KEYS.LAST_ACCOUNT);
  };

  // Auto-reconnect on page load
  useEffect(() => {
    const lastAccount = loadFromStorage(STORAGE_KEYS.LAST_ACCOUNT);
    if (lastAccount && window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then(accounts => {
          if (accounts.includes(lastAccount)) {
            setAccount(lastAccount);
          }
        });
    }

    // Listen for account changes
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          setAccount(accounts[0]);
          saveToStorage(STORAGE_KEYS.LAST_ACCOUNT, accounts[0]);
        }
      };

      const handleChainChanged = () => {
        window.location.reload();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }

    return undefined;
  }, []);

  // ============ Data Loading ============

  const loadUserBalance = useCallback(async () => {
    if (!account) return;

    try {
      const balance = await publicClient.readContract({
        address: config.usdc,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [account]
      });
      setUsdcBalance(formatUnits(balance, 6));
    } catch (err) {
      console.error('Failed to load balance:', err);
    }
  }, [account, config.usdc, publicClient]);

  // Load LP position for connected account
  const loadLPPosition = useCallback(async () => {
    if (!account || config.insurance === '0x0000000000000000000000000000000000000000') {
      setLpPosition(null);
      return;
    }

    try {
      const [position, sharePrice] = await Promise.all([
        publicClient.readContract({
          address: config.insurance,
          abi: INSURANCE_ABI,
          functionName: 'getLPPosition',
          args: [account]
        }),
        publicClient.readContract({
          address: config.insurance,
          abi: INSURANCE_ABI,
          functionName: 'getSharePrice'
        })
      ]);

      setLpPosition({
        shares: position[0].toString(),
        sharesFormatted: formatUnits(position[0], 6),
        value: formatUnits(position[1], 6),
        canWithdraw: position[2],
        sharePrice: formatUnits(sharePrice, 6)
      });
    } catch (err) {
      console.error('Failed to load LP position:', err);
      setLpPosition(null);
    }
  }, [account, config.insurance, publicClient]);

  const loadProtocolStats = useCallback(async () => {
    if (config.insurance === '0x0000000000000000000000000000000000000000') {
      setStats(null);
      setIsPaused(false);
      return;
    }

    try {
      const [result, activeCoverage, paused] = await Promise.all([
        publicClient.readContract({
          address: config.insurance,
          abi: INSURANCE_ABI,
          functionName: 'getStats'
        }),
        publicClient.readContract({
          address: config.insurance,
          abi: INSURANCE_ABI,
          functionName: 'totalActiveCoverage'
        }),
        publicClient.readContract({
          address: config.insurance,
          abi: INSURANCE_ABI,
          functionName: 'paused'
        })
      ]);

      setStats({
        bondReserve: formatUnits(result[0], 6),
        underwriterPool: formatUnits(result[1], 6),
        protocolFees: formatUnits(result[2], 6),
        totalPremiums: formatUnits(result[3], 6),
        policyCount: result[4].toString(),
        activeClaims: result[5].toString(),
        activeCoverage: formatUnits(activeCoverage, 6)
      });

      setIsPaused(paused);
      const canFund = await publicClient.readContract({
        address: config.insurance,
        abi: INSURANCE_ABI,
        functionName: 'canProtocolFundBond'
      });
      setCanProtocolFundBond(canFund);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [config.insurance, publicClient]);

  const loadUserPolicies = useCallback(async () => {
    if (!account) {
      setUserPolicies([]);
      return;
    }

    setLoadingPolicies(true);

    try {
      // Load from local storage
      const storageKey = `${STORAGE_KEYS.POLICIES}_${account}_${network}`;
      const storedPolicies = loadFromStorage(storageKey) || [];

      if (config.insurance === '0x0000000000000000000000000000000000000000') {
        setUserPolicies(storedPolicies);
        setLoadingPolicies(false);
        return;
      }

      // Fetch current state for each stored policy
      const updatedPolicies = await Promise.all(
        storedPolicies.map(async (stored) => {
          try {
            const onchain = await publicClient.readContract({
              address: config.insurance,
              abi: INSURANCE_ABI,
              functionName: 'policies',
              args: [stored.policyId]
            });

            const expirationTime = Number(onchain[5]);
            const settled = onchain[6];
            const active = onchain[7];
            const now = Math.floor(Date.now() / 1000);

            let status = 'unknown';
            if (settled) status = 'paid_out';
            else if (!active) status = 'cancelled';
            else if (expirationTime < now) status = 'expired';
            else status = 'active';

            return {
              ...stored,
              coverageAmount: formatUnits(onchain[1], 6),
              premiumPaid: formatUnits(onchain[2], 6),
              marketId: onchain[3],
              marketQuestion: onchain[4],
              expirationTime,
              settled,
              active,
              status
            };
          } catch {
            return { ...stored, status: 'unknown' };
          }
        })
      );

      setUserPolicies(updatedPolicies);
    } catch (err) {
      console.error('Failed to load policies:', err);
    } finally {
      setLoadingPolicies(false);
    }
  }, [account, config.insurance, network, publicClient]);

  const extractPolymarketSlug = (input) => {
    const normalizeValue = (value) => {
      if (!value) return null;
      const cleaned = value.split(/[?#]/)[0].trim();
      return cleaned ? cleaned : null;
    };

    try {
      const url = new URL(input);
      if (!url.hostname.includes('polymarket.com')) {
        return null;
      }
      const parts = url.pathname.split('/').filter(Boolean);
      const slugSegments = ['event', 'market', 'markets'];
      let slug = null;

      for (const segment of slugSegments) {
        const index = parts.indexOf(segment);
        if (index !== -1 && parts[index + 1]) {
          slug = parts[index + 1];
          break;
        }
      }

      slug = normalizeValue(slug ?? parts.at(-1));
      if (slug) {
        return { type: 'slug', value: slug };
      }

      const queryKeys = ['id', 'market'];
      for (const key of queryKeys) {
        const candidate = normalizeValue(url.searchParams.get(key));
        if (candidate) {
          return { type: 'id', value: candidate };
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const parseMarketLookupInput = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const parsed = extractPolymarketSlug(trimmed);
    if (parsed) {
      return parsed;
    }
    return { type: 'id', value: trimmed };
  };

  const buildPolymarketUrl = (endpoint, path, params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.set('endpoint', endpoint);
    searchParams.set('path', path);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      searchParams.set(key, value);
    });

    return `/api/polymarket?${searchParams.toString()}`;
  };

  const allowedMarketIds = useMemo(() => {
    const ids = new Set();
    const add = (candidate) => {
      if (candidate) {
        ids.add(String(candidate));
      }
    };

    eligibleMarkets.forEach((market) => {
      add(market?.id);
      add(market?.marketId);
      add(market?.market_id);
      add(market?.conditionId);
      add(market?.condition_id);
      add(market?.assetAddress);
      add(market?.asset_address);
      add(market?.assetId);
      add(market?.asset_id);
      add(market?.asset);
      add(market?.slug);
      add(market?.marketSlug);
      add(market?.market_slug);
      add(market?.market?.id);
      add(market?.market?.slug);
      add(market?.market?.conditionId);
    });

    return ids;
  }, [eligibleMarkets]);

  const isMarketAllowed = useCallback((resolvedMarketId, resolvedConditionId, resolvedAssetAddress) => {
    if (!resolvedMarketId && !resolvedConditionId && !resolvedAssetAddress) {
      return false;
    }
    const marketIdValue = resolvedMarketId ? String(resolvedMarketId) : null;
    const conditionIdValue = resolvedConditionId ? String(resolvedConditionId) : null;
    const assetAddressValue = resolvedAssetAddress ? String(resolvedAssetAddress) : null;
    return (
      (marketIdValue && allowedMarketIds.has(marketIdValue)) ||
      (conditionIdValue && allowedMarketIds.has(conditionIdValue)) ||
      (assetAddressValue && allowedMarketIds.has(assetAddressValue))
    );
  }, [allowedMarketIds]);

  const findEligibleMarketMatch = useCallback((parsedInput) => {
    if (!parsedInput?.value) return null;
    const normalizedInput = parsedInput.value.trim().toLowerCase();
    if (!normalizedInput) return null;
    return eligibleMarkets.find((market) => {
      const { marketId, conditionId, slug, assetAddress } = getMarketDetails(market);
      const matches = (value) => value && String(value).toLowerCase() === normalizedInput;
      if (parsedInput.type === 'slug') {
        return matches(slug);
      }
      return matches(conditionId) || matches(marketId) || matches(assetAddress);
    }) ?? null;
  }, [eligibleMarkets]);

  const truncateId = (id) => {
    if (!id) return '';
    const idString = String(id);
    if (idString.length <= 16) {
      return idString;
    }
    return `${idString.slice(0, 10)}...${idString.slice(-6)}`;
  };

  const getMarketDetails = (market) => {
    if (!market) {
      return {
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        assetAddress: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null,
        outcome: null,
        oppositeOutcome: null,
        currentValue: null,
        size: null,
        curPrice: null
      };
    }
    const question = market?.title
      || market?.question
      || market?.market_question
      || market?.name
      || market?.marketTitle
      || market?.market_title
      || market?.market?.question
      || market?.market?.title
      || market?.market?.name;
    const resolvedMarketId = market?.conditionId
      || market?.condition_id
      || market?.id
      || market?.marketId
      || market?.market_id
      || market?.market?.id
      || market?.market?.conditionId;
    const slug = market?.slug || market?.marketSlug || market?.market_slug || market?.market?.slug || null;
    const conditionId = market?.conditionId
      ? String(market.conditionId)
      : (market?.condition_id ? String(market.condition_id) : (market?.market?.conditionId ? String(market.market.conditionId) : null));
    const assetAddress = market?.assetAddress || market?.asset_address || market?.assetId || market?.asset_id || market?.asset || null;
    const rawState = market?.state ?? market?.market?.state ?? market?.market_state ?? market?.market?.market_state ?? null;
    const rawStatus = market?.status ?? market?.market?.status ?? market?.market_status ?? market?.market?.market_status ?? null;
    const normalizedState = rawState ? String(rawState).toLowerCase() : null;
    const normalizedStatus = rawStatus ? String(rawStatus).toLowerCase() : null;
    const resolvedTimestamp = market?.resolvedAt
      ?? market?.resolved_at
      ?? market?.resolutionTime
      ?? market?.resolution_time
      ?? market?.resolvedTime
      ?? market?.resolved_time
      ?? market?.closedTime
      ?? market?.closed_time
      ?? null;
    const nestedResolvedTimestamp = market?.market?.resolvedAt
      ?? market?.market?.resolved_at
      ?? market?.market?.resolutionTime
      ?? market?.market?.resolution_time
      ?? market?.market?.resolvedTime
      ?? market?.market?.resolved_time
      ?? market?.market?.closedTime
      ?? market?.market?.closed_time
      ?? null;
    const rawUmaStatus = market?.umaResolutionStatus
      ?? market?.uma_resolution_status
      ?? market?.market?.umaResolutionStatus
      ?? market?.market?.uma_resolution_status
      ?? null;
    const normalizedUmaStatus = rawUmaStatus ? String(rawUmaStatus).toLowerCase() : null;
    const resolvedByUmaStatus = ['resolved', 'finalized', 'confirmed', 'settled'].includes(normalizedUmaStatus);
    const isClosed = (
      market?.closed === true ||
      market?.closed === 'true' ||
      market?.market?.closed === true ||
      market?.market?.closed === 'true'
    );
    const automaticallyResolved = Boolean(
      market?.automaticallyResolved || market?.market?.automaticallyResolved
    );
    const hasResolvedTimestamp = (
      resolvedTimestamp !== null && resolvedTimestamp !== undefined && resolvedTimestamp !== ''
    ) || (
      nestedResolvedTimestamp !== null && nestedResolvedTimestamp !== undefined && nestedResolvedTimestamp !== ''
    );
    const hasClosedTime = Boolean(
      market?.closedTime ||
      market?.closed_time ||
      market?.market?.closedTime ||
      market?.market?.closed_time
    );
    const umaResolved = normalizedUmaStatus === 'resolved';
    const notAcceptingOrders = (
      market?.acceptingOrders === false ||
      market?.accepting_orders === false ||
      market?.market?.acceptingOrders === false ||
      market?.market?.accepting_orders === false
    );
    const hasResolvedBy = Boolean(
      market?.resolvedBy ||
      market?.resolved_by ||
      market?.market?.resolvedBy ||
      market?.market?.resolved_by
    );
    let pricesShowResolution = false;
    try {
      const prices = market?.outcomePrices || market?.outcome_prices || market?.market?.outcomePrices;
      if (typeof prices === 'string') {
        const parsed = JSON.parse(prices);
        if (Array.isArray(parsed) && parsed.length === 2) {
          const [p1, p2] = parsed.map((price) => parseFloat(price));
          pricesShowResolution = (p1 === 1 && p2 === 0) || (p1 === 0 && p2 === 1);
        }
      }
    } catch {
      // Ignore parse errors
    }
    const hasResolvedFlag = (
      market?.resolved !== undefined ||
      market?.isResolved !== undefined ||
      market?.closed !== undefined ||
      market?.market?.resolved !== undefined ||
      market?.market?.isResolved !== undefined ||
      market?.market?.closed !== undefined ||
      hasResolvedTimestamp ||
      resolvedByUmaStatus ||
      automaticallyResolved ||
      isClosed ||
      hasClosedTime ||
      umaResolved ||
      hasResolvedBy ||
      pricesShowResolution ||
      notAcceptingOrders
    );
    const hasStateStatus = normalizedState !== null || normalizedStatus !== null;
    const hasResolutionSignal = hasResolvedFlag || hasStateStatus;
    const isAuthoritativelyResolved = isClosed || umaResolved;
    const isResolved = isAuthoritativelyResolved
      ? true
      : hasResolutionSignal
        ? Boolean(
          market?.resolved ||
          market?.isResolved ||
          market?.closed ||
          market?.market?.resolved ||
          market?.market?.isResolved ||
          market?.market?.closed ||
          hasResolvedTimestamp ||
          resolvedByUmaStatus ||
          automaticallyResolved ||
          isClosed ||
          hasClosedTime ||
          umaResolved ||
          hasResolvedBy ||
          pricesShowResolution ||
          notAcceptingOrders ||
          normalizedState === 'resolved' ||
          normalizedStatus === 'resolved' ||
          normalizedState === 'closed' ||
          normalizedStatus === 'closed'
        )
        : null;
    
    // Extract end date from Polymarket API response
    const endDateRaw = market?.endDate || market?.endDateIso || market?.end_date || 
                      market?.umaEndDate || market?.umaEndDateIso || null;

    const outcome = market?.outcome ?? market?.positionOutcome ?? market?.market?.outcome ?? null;
    const oppositeOutcome = market?.oppositeOutcome ?? market?.opposite_outcome ?? market?.market?.oppositeOutcome ?? null;
    const currentValue = market?.currentValue ?? market?.current_value ?? market?.market?.currentValue ?? null;
    const size = market?.size ?? market?.positionSize ?? market?.position_size ?? market?.market?.size ?? null;
    const curPrice = market?.curPrice ?? market?.cur_price ?? market?.currentPrice ?? market?.market?.curPrice ?? null;
    
    // Parse to Unix timestamp (seconds)
    let endDateTimestamp = null;
    if (endDateRaw) {
      try {
        const parsed = typeof endDateRaw === 'number' 
          ? endDateRaw 
          : Math.floor(new Date(endDateRaw).getTime() / 1000);
        if (!isNaN(parsed) && parsed > 0) {
          endDateTimestamp = parsed;
        }
      } catch {
        // Invalid date, leave as null
      }
    }
    
    return {
      question,
      marketId: resolvedMarketId ? String(resolvedMarketId) : null,
      slug,
      conditionId,
      assetAddress,
      endDate: endDateRaw,
      endDateTimestamp,
      isResolved,
      outcome,
      oppositeOutcome,
      currentValue,
      size,
      curPrice
    };
  };

  const filteredEligibleMarkets = useMemo(() => (
    eligibleMarkets.filter((market) => getMarketDetails(market).isResolved !== true)
  ), [eligibleMarkets]);

  const isMarketEligible = useCallback((market) => {
    const { marketId, conditionId, slug } = getMarketDetails(market);
    return (
      (marketId && allowedMarketIds.has(marketId)) ||
      (conditionId && allowedMarketIds.has(conditionId)) ||
      (slug && allowedMarketIds.has(slug))
    );
  }, [allowedMarketIds]);

  const selectMarketFromSearch = async (market) => {
    const {
      question,
      marketId: resolvedMarketId,
      slug,
      conditionId,
      assetAddress,
      endDate,
      endDateTimestamp,
      isResolved,
      outcome,
      oppositeOutcome,
      size
    } = getMarketDetails(market);
    if (isResolved === true) {
      setMarketQuestion('');
      setMarketId('');
      setCoverageAmountCap(null);
      setMarketOutcome(null);
      setMarketOppositeOutcome(null);
      setMarketLookupStatus({
        loading: false,
        error: 'This market has already resolved. Coverage cannot be purchased for resolved markets.',
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      return;
    }
    if (!question || !resolvedMarketId) {
      setCoverageAmountCap(null);
      setMarketOutcome(null);
      setMarketOppositeOutcome(null);
      setMarketLookupStatus({
        loading: false,
        error: 'Unable to load selected market details. Try a different market.',
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      return;
    }

    if (conditionId || resolvedMarketId) {
      try {
        const verifyResponse = await fetch(
          buildPolymarketUrl('gamma', `/markets/${conditionId || resolvedMarketId}`),
          { cache: 'no-store' }
        );
        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          const { isResolved: verifiedResolved } = getMarketDetails(verifyData);
          if (verifiedResolved === true) {
            setMarketQuestion('');
            setMarketId('');
            setCoverageAmountCap(null);
            setMarketOutcome(null);
            setMarketOppositeOutcome(null);
            setMarketLookupStatus({
              loading: false,
              error: 'This market has already resolved. Coverage cannot be purchased for resolved markets.',
              source: null,
              question: null,
              marketId: null,
              slug: null,
              conditionId: null,
              endDate: null,
              endDateTimestamp: null,
              isResolved: null
            });
            return;
          }
        }
      } catch (error) {
        console.warn('Resolution verification failed:', error);
      }
    }

    if (!isMarketAllowed(resolvedMarketId, conditionId, assetAddress)) {
      setMarketQuestion('');
      setMarketId('');
      setCoverageAmountCap(null);
      setMarketOutcome(null);
      setMarketOppositeOutcome(null);
      setMarketLookupStatus({
        loading: false,
        error: 'This market is not in your eligible positions.',
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      return;
    }

    const resolvedCoverageCap = (size === null || size === undefined || size === '')
      ? null
      : Number(size);
    const hasCoverageCap = Number.isFinite(resolvedCoverageCap);
    if (hasCoverageCap) {
      setCoverageAmountCap(resolvedCoverageCap);
      setCoverageAmount(String(resolvedCoverageCap));
    } else {
      setCoverageAmountCap(null);
    }
    setMarketOutcome(outcome);
    setMarketOppositeOutcome(oppositeOutcome);
    setMarketInput(String(resolvedMarketId));
    setMarketSearchQuery('');
    setMarketSearchResults([]);
    setMarketQuestion(question);
    setMarketId(String(resolvedMarketId));
    setMarketLookupStatus({
      loading: false,
      error: null,
      source: 'Polymarket search',
      question,
      marketId: String(resolvedMarketId),
      slug,
      conditionId,
      endDate,
      endDateTimestamp,
      isResolved
    });
  };

  const handleCoverageAmountChange = (event) => {
    const { value } = event.target;
    if (value === '') {
      setCoverageAmount(value);
      return;
    }
    if (coverageAmountCap === null || coverageAmountCap === undefined) {
      setCoverageAmount(value);
      return;
    }
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) {
      setCoverageAmount(value);
      return;
    }
    const cappedValue = Math.min(parsedValue, coverageAmountCap);
    setCoverageAmount(String(cappedValue));
  };

  // Market search (Polymarket positions)
  useEffect(() => {
    const trimmedWallet = polymarketWalletAddress.trim();
    if (trimmedWallet && !isAddress(trimmedWallet)) {
      setEligibleMarkets([]);
      setMarketSearchStatus({
        loading: false,
        error: 'Enter a valid EVM address to load your Polymarket positions.'
      });
      return;
    }

    const resolvedWallet = trimmedWallet || account;
    if (!resolvedWallet) {
      setEligibleMarkets([]);
      setMarketSearchStatus({ loading: false, error: null });
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setMarketSearchStatus({ loading: true, error: null });
        const response = await fetch(
          buildPolymarketUrl('data', '/positions', { user: resolvedWallet, sizeThreshold: 0 }),
          { signal: controller.signal, cache: 'no-store' }
        );
        if (!response.ok) {
          throw new Error('Positions request failed');
        }
        const data = await response.json();
        const positions = Array.isArray(data) ? data : data?.data ?? data?.positions ?? [];
        const conditionIds = Array.from(new Set(
          positions
            .map((position) => position?.conditionId ?? position?.condition_id)
            .filter((value) => value !== null && value !== undefined && value !== '')
            .map((value) => String(value))
        ));
        let resolvedByConditionId = new Map();
        if (conditionIds.length > 0) {
          const marketsResponse = await fetch(
            buildPolymarketUrl('gamma', '/markets', { condition_ids: conditionIds }),
            { signal: controller.signal, cache: 'no-store' }
          );
          if (marketsResponse.ok) {
            const marketsData = await marketsResponse.json();
            const marketsList = Array.isArray(marketsData)
              ? marketsData
              : marketsData?.data ?? marketsData?.markets ?? [];
            resolvedByConditionId = new Map(
              marketsList.map((market) => {
                const details = getMarketDetails(market);
                return [details.conditionId, details.isResolved];
              }).filter(([key]) => key)
            );
          }
          const uncheckedConditionIds = conditionIds.filter((id) => !resolvedByConditionId.has(id));
          await Promise.all(
            uncheckedConditionIds.slice(0, 10).map(async (conditionId) => {
              try {
                const response = await fetch(
                  buildPolymarketUrl('gamma', `/markets/${conditionId}`),
                  { signal: controller.signal, cache: 'no-store' }
                );
                if (response.ok) {
                  const data = await response.json();
                  const { isResolved } = getMarketDetails(data);
                  if (isResolved !== null) {
                    resolvedByConditionId.set(conditionId, isResolved);
                  }
                }
              } catch {
                // Ignore individual fetch failures
              }
            })
          );
        }

        const unique = new Map();
        positions.forEach((position) => {
          if (!position) return;
          const { isResolved, conditionId, assetAddress, slug, marketId, endDate, endDateTimestamp } = getMarketDetails(position);
          const resolvedByGamma = conditionId ? resolvedByConditionId.get(String(conditionId)) : undefined;
          const now = Math.floor(Date.now() / 1000);
          const isPastEndDate = endDateTimestamp && endDateTimestamp < now;
          if (resolvedByGamma === true || isResolved === true || isPastEndDate) return;
          const key = String(conditionId ?? assetAddress ?? slug ?? marketId ?? '');
          if (!key || unique.has(key)) return;
          unique.set(key, position);
        });
        setEligibleMarkets(Array.from(unique.values()));
        setMarketSearchStatus({ loading: false, error: null });
      } catch (err) {
        if (err.name === 'AbortError') return;
        setEligibleMarkets([]);
        setMarketSearchStatus({
          loading: false,
          error: 'Unable to load your Polymarket positions right now.'
        });
      }
    }, 400);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [account, polymarketWalletAddress]);

  // Market lookup (Polymarket API) to auto-fill market question
  useEffect(() => {
    if (skipMarketLookupRef.current) {
      skipMarketLookupRef.current = false;
      return;
    }
    const parsedInput = parseMarketLookupInput(marketInput);
    if (!parsedInput) {
      setCoverageAmountCap(null);
      setMarketOutcome(null);
      setMarketOppositeOutcome(null);
      setMarketLookupStatus({
        loading: false,
        error: null,
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      return;
    }

    if (marketSearchStatus.loading) {
      setMarketLookupStatus({
        loading: true,
        error: null,
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      return;
    }

    setMarketLookupStatus({
      loading: true,
      error: null,
      source: null,
      question: null,
      marketId: null,
      slug: null,
      conditionId: null,
      endDate: null,
      endDateTimestamp: null,
      isResolved: null
    });
    const timeout = setTimeout(() => {
      try {
        const market = findEligibleMarketMatch(parsedInput);
        if (!market) {
          throw new Error('Not eligible');
        }
        const {
          question,
          marketId: resolvedMarketId,
          slug,
          conditionId,
          assetAddress,
          endDate,
          endDateTimestamp,
          isResolved,
          size
        } = getMarketDetails(market);

        if (!question || !resolvedMarketId) {
          throw new Error('Market question not found');
        }

        if (!isMarketAllowed(resolvedMarketId, conditionId, assetAddress)) {
          setMarketQuestion('');
          setMarketId('');
          setCoverageAmountCap(null);
          setMarketOutcome(null);
          setMarketOppositeOutcome(null);
          setMarketLookupStatus({
            loading: false,
            error: 'This market is not in your eligible positions.',
            source: null,
            question: null,
            marketId: null,
            slug: null,
            conditionId: null,
            endDate: null,
            endDateTimestamp: null,
            isResolved: null
          });
          return;
        }

        const resolvedCoverageCap = (size === null || size === undefined || size === '')
          ? null
          : Number(size);
        const hasCoverageCap = Number.isFinite(resolvedCoverageCap);
        if (hasCoverageCap) {
          setCoverageAmountCap(resolvedCoverageCap);
          setCoverageAmount(String(resolvedCoverageCap));
        } else {
          setCoverageAmountCap(null);
        }

        setMarketQuestion(question);
        setMarketId(String(resolvedMarketId));
        setMarketLookupStatus({
          loading: false,
          error: null,
          source: 'Polymarket',
          question,
          marketId: String(resolvedMarketId),
          slug,
          conditionId,
          endDate,
          endDateTimestamp,
          isResolved
        });
      } catch (err) {
        const errorMessage = err.message === 'Not eligible'
          ? 'This market is not in your eligible positions.'
          : 'Unable to validate this market. Paste a Polymarket URL, slug, or condition ID.';
        setCoverageAmountCap(null);
        setMarketOutcome(null);
        setMarketOppositeOutcome(null);
        setMarketLookupStatus({
          loading: false,
          error: errorMessage,
          source: null,
          question: null,
          marketId: null,
          slug: null,
          conditionId: null,
          endDate: null,
          endDateTimestamp: null,
          isResolved: null
        });
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [marketInput, marketSearchStatus.loading, findEligibleMarketMatch, isMarketAllowed]);

  // Check if connected wallet is contract owner
  const checkOwnership = useCallback(async () => {
    if (!account || config.insurance === '0x0000000000000000000000000000000000000000') {
      setIsOwner(false);
      return;
    }

    try {
      const owner = await publicClient.readContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'owner'
      });
      const ownerMatch = owner.toLowerCase() === account.toLowerCase();
      setIsOwner(ownerMatch);
      
      if (ownerMatch) {
        loadAdminData();
      }
    } catch (err) {
      console.error('Failed to check ownership:', err);
      setIsOwner(false);
    }
  }, [account, config.insurance, publicClient]);

  // Load data when account or network changes
  useEffect(() => {
    loadProtocolStats();
    loadUserBalance();
    loadUserPolicies();
    loadLPPosition();
    checkOwnership();
  }, [account, network, loadProtocolStats, loadUserBalance, loadUserPolicies, loadLPPosition, checkOwnership]);

  // Load admin-specific data
  const loadAdminData = useCallback(async () => {
    if (config.insurance === '0x0000000000000000000000000000000000000000') return;

    try {
      const [premiumRateBps, minimumBond, contractBalance] = await Promise.all([
        publicClient.readContract({
          address: config.insurance,
          abi: ADMIN_ABI,
          functionName: 'premiumRateBps'
        }),
        publicClient.readContract({
          address: config.insurance,
          abi: ADMIN_ABI,
          functionName: 'getMinimumBond'
        }),
        publicClient.readContract({
          address: config.usdc,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [config.insurance]
        })
      ]);

      setAdminData({
        premiumRate: Number(premiumRateBps) / 100,
        minimumBond: formatUnits(minimumBond, 6),
        contractBalance: formatUnits(contractBalance, 6)
      });
    } catch (err) {
      console.error('Failed to load admin data:', err);
    }
  }, [config.insurance, config.usdc, publicClient]);

  // Admin: Withdraw protocol fees
  const handleWithdrawFees = async () => {
    if (!isOwner) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const walletClient = getWalletClient();
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'withdrawProtocolFees'
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Withdrew $${stats?.protocolFees} in protocol fees!`);
      loadProtocolStats();
      loadAdminData();
    } catch (err) {
      setError('Failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Admin: Withdraw from underwriter pool
  const handleWithdrawFromPool = async (e) => {
    e.preventDefault();
    if (!isOwner || !withdrawPoolAmount) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const walletClient = getWalletClient();
      const amount = parseUnits(withdrawPoolAmount, 6);

      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'withdrawFromPool',
        args: [amount]
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Withdrew $${withdrawPoolAmount} from pool`);
      setWithdrawPoolAmount('');
      loadProtocolStats();
      loadAdminData();
    } catch (err) {
      setError('Failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Admin: Set premium rate
  const handleSetPremiumRate = async (e) => {
    e.preventDefault();
    if (!isOwner || !newPremiumRate) return;

    const rateBps = Math.round(parseFloat(newPremiumRate) * 100);
    if (rateBps < 100 || rateBps > 1000) {
      setError('Premium rate must be between 1% and 10%');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const walletClient = getWalletClient();
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'setPremiumRate',
        args: [BigInt(rateBps)]
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Premium rate updated to ${newPremiumRate}%`);
      setNewPremiumRate('');
      loadAdminData();
    } catch (err) {
      setError('Failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Admin: Cancel policy
  const handleCancelPolicy = async (e) => {
    e.preventDefault();
    if (!isOwner || !cancelPolicyId) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const walletClient = getWalletClient();
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'cancelPolicy',
        args: [cancelPolicyId]
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Policy cancelled: ${cancelPolicyId.slice(0, 10)}...`);
      setCancelPolicyId('');
      loadProtocolStats();
    } catch (err) {
      setError('Failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Admin: Lookup policy details
  const handleLookupPolicy = async (e) => {
    e.preventDefault();
    if (!lookupPolicyId) return;

    setLoading(true);
    setLookupResult(null);

    try {
      const result = await publicClient.readContract({
        address: config.insurance,
        abi: ADMIN_ABI,
        functionName: 'policies',
        args: [lookupPolicyId]
      });

      const now = Math.floor(Date.now() / 1000);
      const expirationTime = Number(result[5]);
      let status = 'unknown';
      if (result[6]) status = 'paid_out';
      else if (!result[7]) status = 'cancelled';
      else if (expirationTime < now) status = 'expired';
      else status = 'active';

      setLookupResult({
        policyholder: result[0],
        coverageAmount: formatUnits(result[1], 6),
        premiumPaid: formatUnits(result[2], 6),
        marketId: result[3],
        marketQuestion: result[4],
        expirationTime: new Date(expirationTime * 1000).toLocaleString(),
        settled: result[6],
        active: result[7],
        status
      });
    } catch (err) {
      setError('Policy not found or invalid ID');
    } finally {
      setLoading(false);
    }
  };

  const computeCoverageDurationDays = useCallback((endDateValue, fallbackTimestamp) => {
    let marketEndTimestamp = null;
    if (endDateValue) {
      try {
        const parsed = typeof endDateValue === 'number'
          ? (endDateValue > 1e12 ? Math.floor(endDateValue / 1000) : endDateValue)
          : Math.floor(new Date(endDateValue).getTime() / 1000);
        if (!Number.isNaN(parsed) && parsed > 0) {
          marketEndTimestamp = parsed;
        }
      } catch {
        // leave null
      }
    }

    if (!marketEndTimestamp && fallbackTimestamp) {
      marketEndTimestamp = fallbackTimestamp;
    }

    const GRACE_PERIOD = 72 * 60 * 60;
    const now = Math.floor(Date.now() / 1000);
    if (!marketEndTimestamp) {
      return {
        marketEndTimestamp: fallbackTimestamp ?? null,
        coverageEndTimestamp: null,
        coverageDurationDays: 30,
        now
      };
    }

    const coverageEndTimestamp = marketEndTimestamp + GRACE_PERIOD;
    const coverageDurationDays = Math.max(1, Math.ceil((coverageEndTimestamp - now) / 86400));

    return {
      marketEndTimestamp,
      coverageEndTimestamp,
      coverageDurationDays,
      now
    };
  }, []);

  const formatUtcTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const formatted = new Date(timestamp * 1000).toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${formatted.replace(', ', ' ')} UTC`;
  };

  // Calculate premium when coverage changes
  useEffect(() => {
    const calculatePremium = async () => {
      if (!coverageAmount || parseFloat(coverageAmount) <= 0) {
        setPremium('0');
        return;
      }

      const { coverageDurationDays, marketEndTimestamp } = computeCoverageDurationDays(
        marketLookupStatus.endDate,
        marketLookupStatus.endDateTimestamp
      );

      // Try contract call first for accurate premium
      try {
        if (config.insurance && config.insurance !== '0x0000000000000000000000000000000000000000') {
          const premiumWei = await publicClient.readContract({
            address: config.insurance,
            abi: INSURANCE_ABI,
            functionName: 'calculatePremium',
            args: [
              parseUnits(coverageAmount || '0', 6),
              BigInt(coverageDurationDays),
              marketId || '',
              BigInt(marketEndTimestamp || 0)
            ]
          });
          setPremium(formatUnits(premiumWei, 6));
          return;
        }
      } catch (err) {
        console.warn('Contract premium calculation failed, using fallback:', err.message);
      }

      // Fallback calculation (mirrors contract logic)
      let basePremium = parseFloat(coverageAmount) * 0.03; // 3% for 30 days
      let durationAdjusted = basePremium * (coverageDurationDays / 30);
      
      // Apply resolution surcharge if market resolves soon
      const now = Math.floor(Date.now() / 1000);
      if (marketEndTimestamp && marketEndTimestamp > now) {
        const timeToResolution = marketEndTimestamp - now;
        if (timeToResolution <= 24 * 60 * 60) {
          // Double premium if resolving within 24 hours
          durationAdjusted = durationAdjusted * 2;
        } else if (timeToResolution <= 72 * 60 * 60) {
          // 1.5x premium if resolving within 72 hours
          durationAdjusted = durationAdjusted * 1.5;
        }
      }
      
      // Minimum premium of $1
      const finalPremium = Math.max(1, durationAdjusted);
      setPremium(finalPremium.toFixed(2));
    };

    calculatePremium();
  }, [
    coverageAmount,
    config.insurance,
    computeCoverageDurationDays,
    marketId,
    marketLookupStatus.endDate,
    marketLookupStatus.endDateTimestamp,
    publicClient
  ]);

  // ============ Wallet Client ============

  const getWalletClient = () => {
    return createWalletClient({
      chain: config.chain,
      transport: custom(window.ethereum),
      account: account
    });
  };

  const approveUSDC = async (amount) => {
    const walletClient = getWalletClient();
    const hash = await walletClient.writeContract({
      address: config.usdc,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [config.insurance, amount]
    });
    await publicClient.waitForTransactionReceipt({ hash });
    return hash;
  };

  // ============ Transactions ============

  const updateTxProgress = (current, total, label) => {
    setTxProgress({ current, total, label });
  };

  const buildUmaAssertionText = (policy) => {
    if (!policy) {
      return 'UMA Assertion: Policy details unavailable.';
    }
    const outcome = policy.outcome || 'N/A';
    const oppositeOutcome = policy.oppositeOutcome || 'N/A';
    const policyQuestion = policy.marketQuestion || 'N/A';
    const policyMarketId = policy.marketId || 'N/A';
    return [
      'UMA Assertion',
      `Market question: ${policyQuestion}`,
      `Market ID: ${policyMarketId}`,
      `Expected outcome: ${outcome}`,
      `Actual resolution: ${oppositeOutcome}`,
      `Evidence summary: ${evidenceSummary || 'N/A'}`,
      `Polymarket link: ${evidencePolymarket || 'N/A'}`,
      `Official/social link: ${evidenceSocial || 'N/A'}`,
      `News/reporting link: ${evidenceNews || 'N/A'}`,
      `Other evidence link: ${evidenceOther || 'N/A'}`
    ].join('\n');
  };

  const buildEvidencePayload = () => {
    return [
      'Evidence Template',
      `Polymarket resolution link: ${evidencePolymarket || 'N/A'}`,
      `Official/social statement link: ${evidenceSocial || 'N/A'}`,
      `News/reporting link: ${evidenceNews || 'N/A'}`,
      `Other evidence link: ${evidenceOther || 'N/A'}`,
      `Summary: ${evidenceSummary || 'N/A'}`,
      buildUmaAssertionText(selectedPolicyForClaim)
    ].join('\n');
  };

  const handleSelectPolicyForClaim = (policy) => {
    setSelectedPolicyForClaim(policy);
    setActualResolution(policy?.oppositeOutcome || '');
    setExpectedResolution(policy?.outcome || '');
  };

  const handlePurchasePolicy = async (e) => {
    e.preventDefault();
    if (!account) {
      setError('Please connect your wallet first');
      return;
    }

    if (!marketLookupStatus.source || !marketLookupStatus.question || !marketLookupStatus.marketId) {
      setError('Please enter a valid Polymarket URL or market ID so we can verify the market question.');
      return;
    }

    if (config.insurance === '0x0000000000000000000000000000000000000000') {
      setError('Contract not deployed yet. Deploy the contract and update the address in App.jsx');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setTxProgress(null);

    try {
      const {
        coverageDurationDays,
        marketEndTimestamp,
        now
      } = computeCoverageDurationDays(marketLookupStatus.endDate, marketLookupStatus.endDateTimestamp);

      if (!marketEndTimestamp) {
        setError('Unable to determine market resolution time. Please revalidate the market.');
        return;
      }

      if (marketLookupStatus.isResolved === true) {
        setError('Market has already resolved; coverage can only be purchased before resolution.');
        return;
      }

      if (marketLookupStatus.isResolved === null || marketLookupStatus.isResolved === undefined) {
        setError('Market resolution status is unavailable. Please revalidate the market before purchasing.');
        return;
      }

      const walletClient = getWalletClient();
      const amount = parseUnits(coverageAmount, 6);
      const premiumAmount = parseUnits(premium, 6);

      const revalidationResponse = await fetch(
        buildPolymarketUrl('gamma', `/markets/${marketLookupStatus.marketId}`),
        { cache: 'no-store' }
      );

      if (!revalidationResponse.ok) {
        throw new Error('Market revalidation failed');
      }

      const revalidationData = await revalidationResponse.json();
      const {
        question: verifiedQuestion,
        marketId: verifiedMarketId,
        slug: verifiedSlug,
        conditionId: verifiedConditionId,
        assetAddress: verifiedAssetAddress,
        isResolved: verifiedIsResolved
      } = getMarketDetails(revalidationData);

      setMarketLookupStatus((prev) => (prev ? { ...prev, isResolved: verifiedIsResolved } : prev));
      if (verifiedIsResolved === null || verifiedIsResolved === undefined) {
        setError('Market resolution status is unavailable after revalidation. Please try again shortly.');
        return;
      }

      if (verifiedIsResolved) {
        setError('Market has already resolved; coverage can only be purchased before resolution.');
        return;
      }

      const detailsMatch = Boolean(
        verifiedQuestion &&
          verifiedMarketId &&
          verifiedQuestion === marketLookupStatus.question &&
          verifiedMarketId === marketLookupStatus.marketId &&
          (!marketLookupStatus.slug || !verifiedSlug || marketLookupStatus.slug === verifiedSlug) &&
          (!marketLookupStatus.conditionId ||
            !verifiedConditionId ||
            marketLookupStatus.conditionId === verifiedConditionId)
      );

      if (!detailsMatch) {
        setError('Market details changed; please revalidate');
        setMarketQuestion('');
        setMarketId('');
        setCoverageAmountCap(null);
        setMarketOutcome(null);
        setMarketOppositeOutcome(null);
        setMarketLookupStatus({
          loading: false,
          error: null,
          source: null,
          question: null,
          marketId: null,
          slug: null,
          conditionId: null,
          endDate: null,
          endDateTimestamp: null,
          isResolved: null
        });
        return;
      }

      if (!isMarketAllowed(verifiedMarketId, verifiedConditionId, verifiedAssetAddress)) {
        setError('This market is not in your eligible positions.');
        setMarketQuestion('');
        setMarketId('');
        setCoverageAmountCap(null);
        setMarketOutcome(null);
        setMarketOppositeOutcome(null);
        setMarketLookupStatus({
          loading: false,
          error: 'This market is not in your eligible positions.',
          source: null,
          question: null,
          marketId: null,
          slug: null,
          conditionId: null,
          endDate: null,
          endDateTimestamp: null,
          isResolved: null
        });
        return;
      }

      if (marketEndTimestamp <= now) {
        setError('Market has already resolved. Coverage can only be purchased before resolution.');
        return;
      }

      if (marketEndTimestamp - now < 24 * 60 * 60) {
        setError('Market resolves within 24 hours. Coverage purchases are disabled for last-minute markets.');
        return;
      }

      // Check and approve USDC
      const allowance = await publicClient.readContract({
        address: config.usdc,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [account, config.insurance]
      });

      if (allowance < premiumAmount) {
        updateTxProgress(1, 2, 'Approving USDC');
        await approveUSDC(premiumAmount);
      }

      updateTxProgress(allowance < premiumAmount ? 2 : 1, allowance < premiumAmount ? 2 : 1, 'Purchasing policy');
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: INSURANCE_ABI,
        functionName: 'purchasePolicy',
        args: [
          parseUnits(coverageAmount, 6),
          marketId,
          marketQuestion,
          BigInt(coverageDurationDays),
          BigInt(marketEndTimestamp),
          marketOutcome
        ]
      });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      // Extract policyId from event logs
      let policyId = null;
      for (const log of receipt.logs) {
        try {
          const decoded = decodeEventLog({
            abi: INSURANCE_ABI,
            data: log.data,
            topics: log.topics
          });
          if (decoded.eventName === 'PolicyPurchased') {
            policyId = decoded.args.policyId;
            break;
          }
        } catch { /* not our event */ }
      }

      // Save to local storage
      const newPolicy = {
        policyId,
        coverageAmount,
        premiumPaid: premium,
        marketId: verifiedMarketId,
        marketQuestion: verifiedQuestion,
        outcome: marketOutcome,
        oppositeOutcome: marketOppositeOutcome,
        duration: coverageDurationDays,
        purchaseDate: Date.now(),
        txHash: hash,
        status: 'active'
      };
      const updated = addPolicyToStorage(newPolicy);
      setUserPolicies(updated);

      setSuccess(`✅ Policy purchased! ID: ${policyId?.slice(0, 10)}...`);
      loadUserBalance();
      loadProtocolStats();

      // Reset form
      setMarketInput('');
      setMarketId('');
      setMarketQuestion('');
      setCoverageAmountCap(null);
      setMarketOutcome(null);
      setMarketOppositeOutcome(null);
      setMarketLookupStatus({
        loading: false,
        error: null,
        source: null,
        question: null,
        marketId: null,
        slug: null,
        conditionId: null,
        endDate: null,
        endDateTimestamp: null,
        isResolved: null
      });
      setTimeout(() => setActiveTab('dashboard'), 2000);
    } catch (err) {
      setError('Transaction failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
      setTxProgress(null);
    }
  };

  const handleSubmitClaim = async (e) => {
    e.preventDefault();
    if (!account) {
      setError('Please connect your wallet first');
      return;
    }

    if (!selectedPolicyForClaim) {
      setError('Please select a policy to dispute');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setTxProgress(null);

    try {
      const walletClient = getWalletClient();

      // If not using protocol bond, user needs to approve bond amount
        if (!useProtocolBondForClaim || !canProtocolFundBond) {
          // Get actual bond for this policy
          const bondAmount = await publicClient.readContract({
            address: config.insurance,
            abi: INSURANCE_ABI,
            functionName: 'calculateBondForPolicy',
            args: [selectedPolicyForClaim.policyId]
          });
        const allowance = await publicClient.readContract({
          address: config.usdc,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [account, config.insurance]
        });

        if (allowance < bondAmount) {
          updateTxProgress(1, 2, 'Approving bond amount');
          await approveUSDC(bondAmount);
        }
      }

      updateTxProgress(
        !useProtocolBondForClaim || !canProtocolFundBond ? 2 : 1,
        !useProtocolBondForClaim || !canProtocolFundBond ? 2 : 1,
        'Submitting dispute to UMA Oracle'
      );
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: INSURANCE_ABI,
        functionName: 'submitClaim',
        args: [
          selectedPolicyForClaim.policyId,
          actualResolution,
          expectedResolution,
          buildEvidencePayload(),
          useProtocolBondForClaim && canProtocolFundBond
        ]
      });

      await publicClient.waitForTransactionReceipt({ hash });

      setSuccess(`✅ Claim submitted! UMA voters will review within 2-48 hours. TX: ${hash.slice(0, 10)}...`);
      loadProtocolStats();
      loadUserPolicies();

      // Reset form
      setSelectedPolicyForClaim(null);
      setActualResolution('');
      setExpectedResolution('');
      setEvidenceSummary('');
      setEvidencePolymarket('');
      setEvidenceSocial('');
      setEvidenceNews('');
      setEvidenceOther('');
    } catch (err) {
      setError('Transaction failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
      setTxProgress(null);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!account) {
      setError('Please connect your wallet first');
      return;
    }

    if (config.insurance === '0x0000000000000000000000000000000000000000') {
      setError('Contract not deployed yet');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setTxProgress(null);

    try {
      const walletClient = getWalletClient();
      const amount = parseUnits(depositAmount, 6);

      const allowance = await publicClient.readContract({
        address: config.usdc,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [account, config.insurance]
      });

      if (allowance < amount) {
        updateTxProgress(1, 2, 'Approving USDC');
        await approveUSDC(amount);
      }

      updateTxProgress(allowance < amount ? 2 : 1, allowance < amount ? 2 : 1, 'Depositing funds');
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: INSURANCE_ABI,
        functionName: 'depositAsUnderwriter',
        args: [amount]
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Deposited ${depositAmount} USDC! You received LP shares.`);
      loadUserBalance();
      loadProtocolStats();
      loadLPPosition();
    } catch (err) {
      setError('Transaction failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
      setTxProgress(null);
    }
  };

  const handleWithdrawLP = async (e) => {
    if (e) e.preventDefault();
    if (!account) {
      setError('Please connect your wallet first');
      return;
    }

    if (!lpPosition || parseFloat(lpPosition.sharesFormatted) === 0) {
      setError('You have no LP shares to withdraw');
      return;
    }

    if (!lpPosition.canWithdraw) {
      setError('7-day cooldown has not passed since your last deposit');
      return;
    }

    // Validate withdrawal amount
    const sharesToWithdraw = withdrawShares ? parseFloat(withdrawShares) : parseFloat(lpPosition.sharesFormatted);
    if (sharesToWithdraw <= 0) {
      setError('Please enter an amount to withdraw');
      return;
    }
    if (sharesToWithdraw > parseFloat(lpPosition.sharesFormatted)) {
      setError('Cannot withdraw more shares than you own');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setTxProgress(null);

    try {
      const walletClient = getWalletClient();
      
      // Calculate shares to burn
      const sharesToBurn = withdrawShares 
        ? parseUnits(withdrawShares, 6) 
        : BigInt(lpPosition.shares);

      // Estimate USDC amount for success message
      const estimatedUSDC = (sharesToWithdraw * parseFloat(lpPosition.sharePrice)).toFixed(2);
      const isPartial = sharesToWithdraw < parseFloat(lpPosition.sharesFormatted);

      updateTxProgress(1, 1, 'Withdrawing LP position');
      const hash = await walletClient.writeContract({
        address: config.insurance,
        abi: INSURANCE_ABI,
        functionName: 'withdrawAsUnderwriter',
        args: [sharesToBurn]
      });

      await publicClient.waitForTransactionReceipt({ hash });
      setSuccess(`✅ Withdrew ~$${estimatedUSDC} USDC${isPartial ? ' (partial withdrawal)' : ''}!`);
      setWithdrawShares('');
      loadUserBalance();
      loadProtocolStats();
      loadLPPosition();
    } catch (err) {
      setError('Transaction failed: ' + (err.shortMessage || err.message));
    } finally {
      setLoading(false);
      setTxProgress(null);
    }
  };

  // ============ UI Helpers ============

  const formatExpiration = (unixTimestamp) => {
    if (!unixTimestamp) return 'Unknown';
    const date = new Date(unixTimestamp * 1000);
    const now = new Date();
    const diff = date - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Expired';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#22c55e';
      case 'expired': return '#f59e0b';
      case 'paid_out': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'expired': return 'Expired';
      case 'paid_out': return 'Paid Out';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const renderTxProgress = () => {
    if (!txProgress) return null;
    const percent = Math.round((txProgress.current / txProgress.total) * 100);

    return (
      <div style={theme.progressContainer}>
        <div style={theme.progressHeader}>
          <span style={theme.progressLabel}>{txProgress.label}</span>
          <span style={theme.progressSteps}>
            Step {txProgress.current} of {txProgress.total}
          </span>
        </div>
        <div style={theme.progressTrack}>
          <div style={{ ...theme.progressFill, width: `${percent}%` }} />
        </div>
      </div>
    );
  };

  // ============ Render ============

  const coverageDetails = useMemo(() => (
    computeCoverageDurationDays(marketLookupStatus.endDate, marketLookupStatus.endDateTimestamp)
  ), [computeCoverageDurationDays, marketLookupStatus.endDate, marketLookupStatus.endDateTimestamp]);
  
  const marketAlreadyResolved = Boolean(
    marketLookupStatus.isResolved ||
    (coverageDetails.marketEndTimestamp &&
      coverageDetails.marketEndTimestamp <= coverageDetails.now)
  );
  const marketResolvesTooSoon = Boolean(
    coverageDetails.marketEndTimestamp &&
    !marketAlreadyResolved &&
    coverageDetails.marketEndTimestamp - coverageDetails.now < 24 * 60 * 60
  );
  const marketResolutionLabel = coverageDetails.marketEndTimestamp
    ? formatUtcTimestamp(coverageDetails.marketEndTimestamp)
    : null;
  const disputeDeadlineLabel = coverageDetails.coverageEndTimestamp
    ? formatUtcTimestamp(coverageDetails.coverageEndTimestamp)
    : null;

  return (
    <div style={theme.container}>
      <div style={theme.bgGradient} />
      
      {/* Header */}
      <header style={theme.header}>
        <div style={theme.logo}>
          <HeaderLogo darkMode={darkMode} />
        </div>
        
        <div style={theme.headerRight}>
          {/* Dark Mode Toggle */}
          <button 
            onClick={toggleDarkMode} 
            style={theme.themeToggle}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
          
          <span style={theme.networkLabel}>💎 Polygon Mainnet</span>
          
          {account ? (
            <div style={theme.accountArea}>
              <div style={theme.balanceBadge}>
                💵 {parseFloat(usdcBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDC
              </div>
              <div style={theme.accountBadge} onClick={disconnectWallet} title="Click to disconnect">
                <span style={theme.accountDot} />
                {account.slice(0, 6)}...{account.slice(-4)}
              </div>
            </div>
          ) : (
            <button onClick={connectWallet} style={theme.connectBtn}>
              Connect Wallet
            </button>
          )}
        </div>
      </header>

      {/* Alerts */}
      {error && (
        <div style={theme.alertError}>
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)} style={theme.alertClose}>×</button>
        </div>
      )}
      {success && (
        <div style={theme.alertSuccess}>
          <span>✓</span> {success}
          <button onClick={() => setSuccess(null)} style={theme.alertClose}>×</button>
        </div>
      )}

      {/* Not Connected State */}
      {!account ? (
        <section style={theme.heroSection}>
          <div style={theme.hero}>
            <h1 style={theme.heroTitle}>
              Coverage for<br />
              <span style={theme.heroAccent}>Prediction Markets</span>
            </h1>
            <p style={theme.heroSubtitle}>
              Protect your Polymarket positions from oracle manipulation and disputed resolutions.
              Powered by UMA's Optimistic Oracle.
            </p>
            <button onClick={connectWallet} style={theme.heroBtn}>
              Connect Wallet to Get Started
            </button>
          </div>

          {/* How It Works */}
          <div style={theme.howItWorks}>
            <h2 style={theme.sectionTitle}>How It Works</h2>
            <p style={theme.stepsNote}>
              Typical premiums: 2–8% depending on coverage amount, time to resolution, and pool conditions.
            </p>
            <div style={theme.stepsGrid}>
              {[
                { num: '1', title: 'Buy Coverage', desc: 'Premium calculated based on risk factors.' },
                { num: '2', title: 'Market Resolves Wrong', desc: 'If manipulated, file a dispute with evidence' },
                { num: '3', title: 'UMA Verifies', desc: 'Token holders review within 2-48 hours' },
                { num: '4', title: 'Get Paid', desc: 'Approved disputes pay out automatically' }
              ].map(step => (
                <div key={step.num} style={theme.step}>
                  <span style={theme.stepNumber}>{step.num}</span>
                  <h3 style={theme.stepTitle}>{step.title}</h3>
                  <p style={theme.stepDesc}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        /* Connected State - Dashboard */
        <main style={theme.main}>
          {/* Tabs */}
          <div style={theme.tabs}>
            {[
              { id: 'dashboard', label: '📊 My Dashboard' },
              { id: 'buy', label: '🛒 Buy Coverage' },
              { id: 'claim', label: '📋 File Dispute' },
              { id: 'underwrite', label: '💰 Provide Liquidity' },
              { id: 'learn', label: '❓ How It Works' },
              ...(isOwner ? [{ id: 'admin', label: '🔐 Admin' }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...theme.tab,
                  ...(activeTab === tab.id ? theme.tabActive : {}),
                  ...(tab.id === 'admin' ? theme.adminTab : {})
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isPaused && (
            <div style={theme.warningBox}>
              ⚠️ Contract is paused. New purchases and deposits are disabled.
            </div>
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div style={theme.dashboard}>
              <div style={theme.dashboardHeader}>
                <h2 style={theme.dashboardTitle}>Dashboard</h2>
                {dashboardInfoDismissed && (
                  <button type="button" onClick={reopenDashboardInfo} style={theme.reopenInfoBtn}>
                    Reopen info
                  </button>
                )}
              </div>

              {!dashboardInfoDismissed && (
                <div style={theme.infoBanner}>
                  <p style={theme.infoBannerText}>
                    ℹ️ Coverage includes 72-hour dispute window after market resolution. Premiums vary by risk
                    factors.{' '}
                    <button type="button" onClick={() => setActiveTab('learn')} style={theme.infoBannerLink}>
                      Learn more →
                    </button>
                  </p>
                  <button type="button" onClick={dismissDashboardInfo} style={theme.infoBannerDismiss}>
                    Dismiss
                  </button>
                </div>
              )}

              {/* Protocol Stats */}
              <div style={theme.statsGrid}>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>
                    ${stats
                      ? Math.max(
                        0,
                        parseFloat(stats.underwriterPool) - parseFloat(stats.activeCoverage || 0)
                      ).toLocaleString()
                      : '---'}
                  </span>
                  <span style={theme.statLabel}>Available Coverage</span>
                </div>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>{stats?.policyCount || '---'}</span>
                  <span style={theme.statLabel}>Total Policies</span>
                </div>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>
                    ${stats ? parseFloat(stats.activeCoverage || 0).toLocaleString() : '---'}
                  </span>
                  <span style={theme.statLabel}>Active Coverage</span>
                </div>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>
                    ${stats ? parseFloat(stats.bondReserve).toLocaleString() : '---'}
                  </span>
                  <span style={theme.statLabel}>Bond Reserve</span>
                </div>
                <div style={theme.statCard}>
                  <span style={{...theme.statValue, color: canProtocolFundBond ? '#22c55e' : '#f59e0b'}}>
                    {canProtocolFundBond ? '✓ Yes' : '✗ No'}
                  </span>
                  <span style={theme.statLabel}>Free Claims</span>
                </div>
              </div>

              {/* My Policies Section */}
              <div style={theme.section}>
                <div style={theme.sectionHeader}>
                  <h2 style={theme.sectionTitle2}>My Policies</h2>
                  <button onClick={loadUserPolicies} style={theme.refreshBtn} disabled={loadingPolicies}>
                    {loadingPolicies ? '⏳' : '🔄'} Refresh
                  </button>
                </div>

                {userPolicies.length === 0 ? (
                  <div style={theme.emptyState}>
                    <span style={theme.emptyIcon}>📋</span>
                    <p style={theme.emptyText}>No policies yet</p>
                    <p style={theme.emptySubtext}>Purchase coverage to protect your prediction market positions</p>
                    <button onClick={() => setActiveTab('buy')} style={theme.emptyBtn}>
                      Buy Your First Coverage
                    </button>
                  </div>
                ) : (
                  <div style={theme.policiesList}>
                    {userPolicies.map((policy, idx) => (
                      <div key={policy.policyId || idx} style={theme.policyCard}>
                        <div style={theme.policyHeader}>
                          <span style={{...theme.statusBadge, background: getStatusColor(policy.status)}}>
                            {getStatusLabel(policy.status)}
                          </span>
                          <span style={theme.policyId}>
                            ID: {policy.policyId?.slice(0, 10)}...
                          </span>
                        </div>
                        <div style={theme.policyBody}>
                          <p style={theme.policyQuestion}>
                            {policy.marketQuestion || 'Loading...'}
                          </p>
                          <div style={theme.policyStats}>
                            <div>
                              <span style={theme.policyStatLabel}>Coverage</span>
                              <span style={theme.policyStatValue}>
                                ${parseFloat(policy.coverageAmount).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span style={theme.policyStatLabel}>Premium</span>
                              <span style={theme.policyStatValue}>${policy.premiumPaid}</span>
                            </div>
                            <div>
                              <span style={theme.policyStatLabel}>Expires</span>
                              <span style={theme.policyStatValue}>
                                {policy.expirationTime 
                                  ? formatExpiration(policy.expirationTime) 
                                  : `${policy.duration} days`}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={theme.policyActions}>
                          {policy.status === 'active' && (
                            <button 
                              onClick={() => {
                                setSelectedPolicyForClaim(policy);
                                setActiveTab('claim');
                              }}
                              style={theme.claimBtn}
                            >
                              File Dispute
                            </button>
                          )}
                          {policy.txHash && (
                            <a 
                              href={`${config.explorer}/tx/${policy.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={theme.viewTxLink}
                            >
                              View TX ↗
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buy Coverage Tab */}
          {activeTab === 'buy' && (
            <div style={theme.tabContent}>
              <h2 style={theme.formTitle}>Purchase Coverage</h2>
              <form onSubmit={handlePurchasePolicy} style={theme.form}>
                {renderTxProgress()}
                <div style={theme.formGroup}>
                  <label style={theme.label}>Coverage Amount (USDC)</label>
                  <input
                    type="number"
                    value={coverageAmount}
                    onChange={handleCoverageAmountChange}
                    placeholder="5000"
                    style={theme.input}
                    min="100"
                    max={coverageAmountCap ?? undefined}
                    required
                  />
                  <span style={theme.hint}>Maximum payout if your dispute is approved</span>
                </div>

                <div style={theme.formGroup}>
                  <label style={theme.label}>Polymarket Wallet Address</label>
                  <input
                    type="text"
                    value={polymarketWalletAddress}
                    onChange={(e) => setPolymarketWalletAddress(e.target.value)}
                    placeholder={account || '0x...'}
                    style={theme.input}
                  />
                  <span style={theme.hint}>
                    Find it on your Polymarket profile, settings, or deposit page.
                    {' '}
                    <a
                      href="https://polymarket.com/profile"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={theme.inlineLink}
                    >
                      Open Polymarket ↗
                    </a>
                  </span>
                </div>

                <div style={theme.formGroup}>
                  <label style={theme.label}>Your Markets</label>
                  <span style={theme.hint}>
                    We pull your positions from Polymarket so you can quickly select a market you own.
                  </span>
                  <span style={theme.hint}>
                    The Polymarket wallet address is used for fetching positions.
                  </span>
                  <span style={theme.hint}>
                    Your connected browser wallet is used for signing transactions and paying premiums.
                  </span>
                  {marketSearchStatus.loading && (
                    <span style={theme.hint}>🔎 Loading your positions...</span>
                  )}
                  {marketSearchStatus.error && (
                    <span style={theme.hintWarning}>⚠️ {marketSearchStatus.error}</span>
                  )}
                  {!marketSearchStatus.loading && !marketSearchStatus.error && filteredEligibleMarkets.length > 0 && (
                    <span style={theme.hintSuccess}>
                      ✅ Found {filteredEligibleMarkets.length} eligible market{filteredEligibleMarkets.length === 1 ? '' : 's'}.
                    </span>
                  )}
                  {!marketSearchStatus.loading && !marketSearchStatus.error && filteredEligibleMarkets.length === 0 && (
                    <span style={theme.hint}>No eligible positions found yet. Use the manual URL or ID input below.</span>
                  )}
                  {filteredEligibleMarkets.length > 0 && (
                    <div style={theme.marketGrid}>
                      {filteredEligibleMarkets
                        .map((market, index) => {
                          const details = getMarketDetails(market);
                          const marketKey = details.marketId || details.conditionId || details.slug || market?.id || market?.conditionId || market?.slug || `market-${index}`;
                          return (
                            <button
                              key={marketKey}
                              type="button"
                              onClick={() => selectMarketFromSearch(market)}
                              style={theme.searchResultBtn}
                            >
                              <div style={theme.searchResultQuestion}>
                                {details.question || 'Untitled market'}
                              </div>
                              <div style={theme.searchResultMeta}>
                                {details.slug && <span>Slug: {details.slug}</span>}
                                {details.marketId && <span>ID: {truncateId(details.marketId)}</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={theme.formGroup}>
                  <label style={theme.label}>Polymarket Market ID or URL</label>
                  <input
                    type="text"
                    value={marketInput}
                    onChange={(e) => {
                      setMarketInput(e.target.value);
                      setMarketId('');
                      setMarketQuestion('');
                      setCoverageAmountCap(null);
                      setMarketOutcome(null);
                      setMarketOppositeOutcome(null);
                      setMarketLookupStatus({
                        loading: false,
                        error: null,
                        source: null,
                        question: null,
                        marketId: null,
                        slug: null,
                        conditionId: null,
                        endDate: null,
                        endDateTimestamp: null,
                        isResolved: null
                      });
                    }}
                    placeholder="https://polymarket.com/event/... or market ID"
                    style={theme.input}
                    required
                  />
                  <span style={theme.hint}>
                    Paste a Polymarket event URL or market ID. We'll resolve the official question for you.
                  </span>
                  {marketLookupStatus.loading && (
                    <span style={theme.hint}>🔎 Validating market...</span>
                  )}
                  {marketLookupStatus.error && (
                    <span style={theme.hintWarning}>⚠️ {marketLookupStatus.error}</span>
                  )}
                  {!marketLookupStatus.loading && marketLookupStatus.source && (
                    <span style={theme.hintSuccess}>
                      ✅ Market question loaded from {marketLookupStatus.source}
                    </span>
                  )}
                </div>

                <div style={theme.formGroup}>
                  <label style={theme.label}>Market Question</label>
                  <textarea
                    value={marketQuestion}
                    placeholder="Will ETH reach $10,000 by December 2026?"
                    style={{ ...theme.input, minHeight: '80px', resize: 'vertical' }}
                    readOnly
                    required
                  />
                  <span style={theme.hint}>
                    Auto-filled from Polymarket after validation.
                  </span>
                </div>

                <div style={theme.formGroup}>
                  <label style={theme.label}>Coverage Window</label>
                  {marketResolutionLabel ? (
                    <>
                      <div style={theme.hint}>Market resolves: {marketResolutionLabel}</div>
                      {disputeDeadlineLabel && (
                        <div style={theme.hint}>
                          Dispute deadline: {disputeDeadlineLabel} (72hr grace period)
                        </div>
                      )}
                      <div style={theme.hint}>
                        Coverage duration: {coverageDetails.coverageDurationDays} days
                      </div>
                    </>
                  ) : (
                    <div style={theme.hintWarning}>⚠️ Market resolution time unavailable.</div>
                  )}
                  {marketAlreadyResolved && (
                    <div style={theme.hintWarning}>⚠️ Market has already resolved. You can still file disputes for existing coverage.</div>
                  )}
                  {marketResolvesTooSoon && !marketAlreadyResolved && (
                    <div style={theme.hintWarning}>⚠️ Market resolves within 24 hours. Coverage may have limited value.</div>
                  )}
                </div>

                <div style={theme.premiumBox}>
                  <div style={theme.premiumRow}>
                    <span>Premium</span>
                    <span style={theme.premiumValue}>
                      ${parseFloat(premium).toFixed(2)} USDC
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginLeft: '8px' }}>
                        ({coverageAmount && parseFloat(coverageAmount) > 0 
                          ? ((parseFloat(premium) / parseFloat(coverageAmount)) * 100).toFixed(2) 
                          : '0'}% of coverage)
                      </span>
                    </span>
                  </div>
                  <div style={theme.premiumRow}>
                    <span>Max Payout</span>
                    <span>${parseFloat(coverageAmount || 0).toLocaleString()} USDC</span>
                  </div>
                  <div style={theme.premiumRow}>
                    <span>Coverage Duration</span>
                    <span>{coverageDetails.coverageDurationDays} days</span>
                  </div>
                  <div style={{...theme.premiumRow, borderBottom: 'none'}}>
                    <span>Your Balance</span>
                    <span style={{color: parseFloat(usdcBalance) >= parseFloat(premium) ? '#22c55e' : '#ef4444'}}>
                      ${parseFloat(usdcBalance).toLocaleString()} USDC
                    </span>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={
                    loading ||
                    parseFloat(usdcBalance) < parseFloat(premium) ||
                    !marketLookupStatus.source ||
                    marketLookupStatus.loading ||
                    marketLookupStatus.error ||
                    !marketLookupStatus.question ||
                    !marketLookupStatus.marketId ||
                    marketResolvesTooSoon ||
                    marketAlreadyResolved
                  }
                  style={{
                    ...theme.submitBtn,
                    opacity: (
                      loading ||
                      parseFloat(usdcBalance) < parseFloat(premium) ||
                      !marketLookupStatus.source ||
                      marketLookupStatus.loading ||
                      marketLookupStatus.error ||
                      !marketLookupStatus.question ||
                      !marketLookupStatus.marketId ||
                      marketResolvesTooSoon ||
                      marketAlreadyResolved
                    ) ? 0.5 : 1
                  }}
                >
                  {loading ? '⏳ Processing...' : `Pay $${parseFloat(premium).toFixed(2)} USDC for Coverage`}
                </button>
                <div style={theme.disclaimerBox}>
                  <strong>Not insurance:</strong> Rug Shield is a peer-to-peer dispute resolution protocol. 
                  No insurance policy or contract is created, no underwriting occurs, and no party guarantees payments. 
                  Contributions are not insurance premiums; any disbursement depends on UMA Oracle resolution and available pool liquidity.
                </div>
              </form>
            </div>
          )}

          {/* File Claim Tab */}
          {activeTab === 'claim' && (
            <div style={theme.tabContent}>
              <h2 style={theme.formTitle}>File a Dispute</h2>
              
              {userPolicies.filter(p => p.status === 'active').length === 0 ? (
                <div style={theme.emptyState}>
                  <span style={theme.emptyIcon}>📋</span>
                  <p style={theme.emptyText}>No active coverage to dispute</p>
                  <button onClick={() => setActiveTab('buy')} style={theme.emptyBtn}>
                    Buy Coverage First
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitClaim} style={theme.form}>
                  {renderTxProgress()}
                  <div style={theme.formGroup}>
                    <label style={theme.label}>Select Coverage to Dispute</label>
                    <div style={theme.policySelector}>
                      {userPolicies.filter(p => p.status === 'active').map((policy, idx) => (
                        <div 
                          key={policy.policyId || idx}
                          onClick={() => handleSelectPolicyForClaim(policy)}
                          style={{
                            ...theme.policySelectorItem,
                            ...(selectedPolicyForClaim?.policyId === policy.policyId 
                              ? theme.policySelectorItemActive 
                              : {})
                          }}
                        >
                          <div style={theme.policySelectorQuestion}>
                            {policy.marketQuestion}
                          </div>
                          <div style={theme.policySelectorDetails}>
                            ${parseFloat(policy.coverageAmount).toLocaleString()} coverage • 
                            Expires {policy.expirationTime 
                              ? formatExpiration(policy.expirationTime) 
                              : `in ${policy.duration} days`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedPolicyForClaim && (
                    <>
                      <div style={theme.formRow}>
                        <div style={theme.formGroup}>
                          <label style={theme.label}>Market Resolved As</label>
                          <input
                            type="text"
                            value={actualResolution}
                            onChange={(e) => setActualResolution(e.target.value)}
                            placeholder="YES"
                            style={theme.input}
                            required
                          />
                        </div>
                        <div style={theme.formGroup}>
                          <label style={theme.label}>Should Have Been</label>
                          <input
                            type="text"
                            value={expectedResolution}
                            onChange={(e) => setExpectedResolution(e.target.value)}
                            placeholder="NO"
                            style={theme.input}
                            required
                          />
                        </div>
                      </div>

                      <div style={theme.formGroup}>
                        <label style={theme.label}>Evidence Submission Template</label>
                        <div style={theme.evidenceGrid}>
                          <div style={theme.formGroup}>
                            <label style={theme.labelSmall}>Polymarket Resolution Link</label>
                            <input
                              type="url"
                              value={evidencePolymarket}
                              onChange={(e) => setEvidencePolymarket(e.target.value)}
                              placeholder="https://polymarket.com/..."
                              style={theme.input}
                            />
                          </div>
                          <div style={theme.formGroup}>
                            <label style={theme.labelSmall}>Official/Social Statement</label>
                            <input
                              type="url"
                              value={evidenceSocial}
                              onChange={(e) => setEvidenceSocial(e.target.value)}
                              placeholder="https://x.com/... or official blog post"
                              style={theme.input}
                            />
                          </div>
                          <div style={theme.formGroup}>
                            <label style={theme.labelSmall}>News or Reporting Link</label>
                            <input
                              type="url"
                              value={evidenceNews}
                              onChange={(e) => setEvidenceNews(e.target.value)}
                              placeholder="https://news-site.com/article"
                              style={theme.input}
                            />
                          </div>
                          <div style={theme.formGroup}>
                            <label style={theme.labelSmall}>Additional Evidence</label>
                            <input
                              type="url"
                              value={evidenceOther}
                              onChange={(e) => setEvidenceOther(e.target.value)}
                              placeholder="https://ipfs.io/... or other proof"
                              style={theme.input}
                            />
                          </div>
                        </div>
                        <div style={theme.formGroup}>
                          <label style={theme.labelSmall}>Summary (Why the resolution is wrong)</label>
                          <textarea
                            value={evidenceSummary}
                            onChange={(e) => setEvidenceSummary(e.target.value)}
                            placeholder="Concise summary referencing the links above"
                            style={{ ...theme.input, minHeight: '120px', resize: 'vertical' }}
                            required
                          />
                        </div>
                        <span style={theme.hint}>
                          Provide verifiable links and a short summary. UMA voters rely on structured evidence.
                        </span>
                      </div>

                      <div style={theme.checkboxGroup}>
                        <label style={theme.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={useProtocolBondForClaim && canProtocolFundBond}
                            onChange={(e) => setUseProtocolBondForClaim(e.target.checked)}
                            style={theme.checkbox}
                            disabled={!canProtocolFundBond}
                          />
                          <span>Use protocol bond (free dispute filing)</span>
                        </label>
                        {!canProtocolFundBond && (
                          <span style={theme.hintWarning}>
                            ⚠️ Protocol bond not available yet. You'll need to post $750 USDC bond 
                            (refunded if your dispute is approved).
                          </span>
                        )}
                      </div>

                      <div style={theme.warningBox}>
                        <strong>⚠️ Important:</strong> Disputes are reviewed by UMA token holders who vote on whether 
                        your evidence supports the dispute. False or unsubstantiated disputes will lose the $750 bond. 
                        Only file if you have clear, verifiable evidence.
                      </div>

                      <button 
                        type="submit" 
                        disabled={loading}
                        style={{
                          ...theme.submitBtn,
                          opacity: loading ? 0.5 : 1
                        }}
                      >
                        {loading ? '⏳ Processing...' : 'Submit Dispute to UMA Oracle'}
                      </button>
                      <div style={theme.disclaimerBox}>
                        <strong>Not insurance:</strong> Rug Shield does not provide insurance or claims payments. 
                        No party underwrites or guarantees outcomes, and disputes are resolved solely by UMA Oracle voters. 
                        Disbursements are not insurance claims payments and depend on oracle approval and pool liquidity.
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          )}

          {/* Underwrite Tab */}
          {activeTab === 'underwrite' && (
            <div style={theme.tabContent}>
              <h2 style={theme.formTitle}>Provide Liquidity & Earn Yield</h2>
              
              {/* Quick Stats Bar - Show value proposition immediately */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '10px',
                marginBottom: '20px'
              }}>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>{poolSnapshot.totalPool}</span>
                  <span style={theme.statLabel}>Total Pool</span>
                </div>
                <div style={theme.statCard}>
                  <span style={{...theme.statValue, color: '#22c55e'}}>{poolSnapshot.currentApy}</span>
                  <span style={theme.statLabel}>Current APY</span>
                </div>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>{poolSnapshot.utilization}</span>
                  <span style={theme.statLabel}>Utilization</span>
                </div>
                <div style={theme.statCard}>
                  <span style={theme.statValue}>50%</span>
                  <span style={theme.statLabel}>Premium Share</span>
                </div>
              </div>

              {/* Deposit Form - Action First! */}
              <form onSubmit={handleDeposit} style={{...theme.form, marginBottom: '20px'}}>
                {renderTxProgress()}
                <div style={theme.formGroup}>
                  <label style={theme.label}>Deposit Amount (USDC)</label>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="1000"
                    style={theme.input}
                    min="100"
                    required
                  />
                  <span style={theme.hint}>
                    Minimum $100 • Your balance: ${parseFloat(usdcBalance).toLocaleString()} USDC
                  </span>
                </div>

                <button 
                  type="submit" 
                  disabled={loading || parseFloat(usdcBalance) < parseFloat(depositAmount) || parseFloat(depositAmount) < 100}
                  style={{
                    ...theme.submitBtn,
                    opacity: (loading || parseFloat(usdcBalance) < parseFloat(depositAmount) || parseFloat(depositAmount) < 100) ? 0.5 : 1
                  }}
                >
                  {loading ? '⏳ Processing...' : `Deposit $${depositAmount || '0'} USDC`}
                </button>
                <div style={{...theme.hint, marginTop: '8px', textAlign: 'center'}}>
                  Earn 50% of premiums • 7-day withdrawal cooldown • Fully on-chain
                </div>
              </form>

              {/* Current LP Position - Show if they have one */}
              {lpPosition && parseFloat(lpPosition.value) > 0 && (
                <div style={theme.lpPositionBox}>
                  <h3 style={theme.lpPositionTitle}>📊 Your LP Position</h3>
                  <div style={theme.lpStatsGrid}>
                    <div style={theme.lpStatItem}>
                      <span style={theme.lpStatLabel}>Your Shares</span>
                      <span style={theme.lpStatValue}>{parseFloat(lpPosition.sharesFormatted).toLocaleString()}</span>
                    </div>
                    <div style={theme.lpStatItem}>
                      <span style={theme.lpStatLabel}>Current Value</span>
                      <span style={{...theme.lpStatValue, color: '#22c55e'}}>
                        ${parseFloat(lpPosition.value).toLocaleString()}
                      </span>
                    </div>
                    <div style={theme.lpStatItem}>
                      <span style={theme.lpStatLabel}>Share Price</span>
                      <span style={theme.lpStatValue}>${lpPosition.sharePrice}</span>
                    </div>
                    <div style={theme.lpStatItem}>
                      <span style={theme.lpStatLabel}>
                        {parseFloat(lpPosition.sharePrice) > 1 ? 'Est. Profit' : 'Can Withdraw'}
                      </span>
                      <span style={{...theme.lpStatValue, color: lpPosition.canWithdraw ? '#22c55e' : '#f59e0b'}}>
                        {parseFloat(lpPosition.sharePrice) > 1 
                          ? `+$${((parseFloat(lpPosition.sharePrice) - 1) * parseFloat(lpPosition.sharesFormatted)).toFixed(2)}`
                          : (lpPosition.canWithdraw ? '✓ Yes' : '⏳ 7-day lock')
                        }
                      </span>
                    </div>
                  </div>
                  
                  {/* Withdraw Section */}
                  <div style={theme.withdrawSection}>
                    {/* Quick Actions */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <button
                        type="button"
                        onClick={() => setWithdrawShares(lpPosition.sharesFormatted)}
                        disabled={!lpPosition.canWithdraw}
                        style={{
                          ...theme.adminBtn,
                          flex: '1 1 auto',
                          textAlign: 'center',
                          opacity: lpPosition.canWithdraw ? 1 : 0.5
                        }}
                      >
                        100% (${parseFloat(lpPosition.value).toLocaleString()})
                      </button>
                      <button
                        type="button"
                        onClick={() => setWithdrawShares((parseFloat(lpPosition.sharesFormatted) / 2).toFixed(6))}
                        disabled={!lpPosition.canWithdraw}
                        style={{
                          ...theme.adminBtn,
                          flex: '1 1 auto',
                          textAlign: 'center',
                          opacity: lpPosition.canWithdraw ? 1 : 0.5
                        }}
                      >
                        50% (${(parseFloat(lpPosition.value) / 2).toLocaleString()})
                      </button>
                      {parseFloat(lpPosition.sharePrice) > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            // Calculate shares needed to withdraw just the profit
                            const profitUSD = (parseFloat(lpPosition.sharePrice) - 1) * parseFloat(lpPosition.sharesFormatted);
                            const sharesToWithdraw = profitUSD / parseFloat(lpPosition.sharePrice);
                            setWithdrawShares(sharesToWithdraw.toFixed(6));
                          }}
                          disabled={!lpPosition.canWithdraw}
                          style={{
                            ...theme.adminBtn,
                            ...theme.adminBtnGreen,
                            flex: '1 1 auto',
                            textAlign: 'center',
                            opacity: lpPosition.canWithdraw ? 1 : 0.5
                          }}
                        >
                          Profits Only (+${((parseFloat(lpPosition.sharePrice) - 1) * parseFloat(lpPosition.sharesFormatted)).toFixed(2)})
                        </button>
                      )}
                    </div>

                    {/* Custom Amount Input */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{...theme.hint, display: 'block', marginBottom: '4px'}}>
                          Shares to withdraw
                        </label>
                        <input
                          type="number"
                          value={withdrawShares}
                          onChange={(e) => setWithdrawShares(e.target.value)}
                          placeholder="0.00"
                          max={lpPosition.sharesFormatted}
                          step="0.000001"
                          style={{...theme.input, width: '100%'}}
                          disabled={!lpPosition.canWithdraw}
                        />
                      </div>
                      <button 
                        onClick={handleWithdrawLP}
                        disabled={loading || !lpPosition.canWithdraw || !withdrawShares || parseFloat(withdrawShares) <= 0}
                        style={{
                          ...theme.withdrawBtn,
                          opacity: (!lpPosition.canWithdraw || !withdrawShares || parseFloat(withdrawShares) <= 0) ? 0.5 : 1,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {loading ? '⏳...' : `Withdraw ~$${withdrawShares ? (parseFloat(withdrawShares) * parseFloat(lpPosition.sharePrice)).toFixed(2) : '0'}`}
                      </button>
                    </div>
                    
                    {!lpPosition.canWithdraw && (
                      <span style={theme.cooldownHint}>
                        7-day cooldown protects against flash loan attacks
                      </span>
                    )}
                    {withdrawShares && parseFloat(withdrawShares) > parseFloat(lpPosition.sharesFormatted) && (
                      <span style={{...theme.hintWarning, textAlign: 'center'}}>
                        ⚠️ Cannot withdraw more shares than you own
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Collapsible Details Section */}
              <details style={{
                background: theme.section.background,
                border: theme.section.border,
                borderRadius: '12px',
                marginTop: '16px'
              }}>
                <summary style={{
                  padding: '14px 16px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: theme.tab.color,
                  listStyle: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>📚 Learn More About Providing Liquidity</span>
                  <span style={{fontSize: '12px', opacity: 0.7}}>▼</span>
                </summary>
                <div style={{padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <div>
                    <h4 style={{margin: '0 0 8px', fontSize: '14px'}}>How It Works</h4>
                    <ol style={theme.underwriteList}>
                      <li><strong>Deposit USDC</strong> — Minimum $100, no maximum</li>
                      <li><strong>Earn Premiums</strong> — 50% of all premiums flow to LPs</li>
                      <li><strong>Withdraw Anytime</strong> — 7-day cooldown after deposit</li>
                    </ol>
                  </div>
                  
                  <div>
                    <h4 style={{margin: '0 0 8px', fontSize: '14px'}}>Risk Disclosure</h4>
                    <p style={theme.underwriteParagraph}>
                      Your capital backs coverage policies. If disputes are approved, payouts come from the pool:
                    </p>
                    <ul style={theme.underwriteList}>
                      <li><strong>Premiums &gt; payouts:</strong> Your share grows (profit)</li>
                      <li><strong>Payouts &gt; premiums:</strong> Your share shrinks (loss)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 style={{margin: '0 0 8px', fontSize: '14px'}}>Fee Structure</h4>
                    <p style={theme.underwriteParagraph}>
                      Premium allocation: 50% to LPs, 30% to bond reserve, 20% to protocol
                    </p>
                  </div>

                  <div>
                    <h4 style={{margin: '0 0 8px', fontSize: '14px'}}>Why Provide Liquidity?</h4>
                    <ul style={theme.underwriteList}>
                      <li><strong>Passive yield</strong> from coverage premiums</li>
                      <li><strong>Uncorrelated returns</strong> — not tied to crypto prices</li>
                      <li><strong>Transparent</strong> — all transactions on-chain</li>
                      <li><strong>Liquid</strong> — withdraw after 7-day cooldown</li>
                    </ul>
                  </div>
                </div>
              </details>
            </div>
          )}

          {activeTab === 'learn' && (
            <div style={theme.tabContent}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={theme.section}>
                  <h2 style={theme.sectionTitle2}>How Rug Shield Works</h2>
                  <ReactMarkdown>{learnContent.flow}</ReactMarkdown>
                </div>

                <div style={theme.section}>
                  <h2 style={theme.sectionTitle2}>Pricing</h2>
                  <ReactMarkdown>{learnContent.pricing}</ReactMarkdown>
                </div>

                <div style={theme.section}>
                  <h2 style={theme.sectionTitle2}>Dispute Requirements</h2>
                  <ReactMarkdown>{learnContent.dispute}</ReactMarkdown>
                </div>

                <div style={theme.section}>
                  <h2 style={theme.sectionTitle2}>What's NOT Covered</h2>
                  <ReactMarkdown>{learnContent.notCovered}</ReactMarkdown>
                </div>

                <div style={theme.infoBox}>
                  <h3 style={theme.infoTitle}>📎 Review the Legal Docs</h3>
                  <p style={{ margin: '0 0 10px', fontSize: '13px' }}>
                    Read the full coverage terms and platform agreement before purchasing.
                  </p>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      style={theme.footerButton}
                      onClick={() => setLegalDoc('coverage')}
                    >
                      Coverage Terms
                    </button>
                    <button
                      type="button"
                      style={theme.footerButton}
                      onClick={() => setLegalDoc('terms')}
                    >
                      Terms of Service
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Portal Tab - Only visible to contract owner */}
          {activeTab === 'admin' && isOwner && (
            <div style={theme.tabContent}>
              <div style={theme.adminHeader}>
                <h2 style={theme.formTitle}>🔐 Admin Portal</h2>
                <span style={theme.ownerBadge}>Owner: {account?.slice(0, 6)}...{account?.slice(-4)}</span>
              </div>

              {/* Protocol Overview */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>📊 Protocol Overview</h3>
                <div style={theme.adminStatsGrid}>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Contract Balance</span>
                    <span style={theme.adminStatValue}>
                      ${adminData ? parseFloat(adminData.contractBalance).toLocaleString() : '---'}
                    </span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Underwriter Pool</span>
                    <span style={theme.adminStatValue}>
                      ${stats ? parseFloat(stats.underwriterPool).toLocaleString() : '---'}
                    </span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Bond Reserve</span>
                    <span style={theme.adminStatValue}>
                      ${stats ? parseFloat(stats.bondReserve).toLocaleString() : '---'}
                    </span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Claimable Fees</span>
                    <span style={{...theme.adminStatValue, color: '#22c55e'}}>
                      ${stats ? parseFloat(stats.protocolFees).toLocaleString() : '---'}
                    </span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Total Policies</span>
                    <span style={theme.adminStatValue}>{stats?.policyCount || '---'}</span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Active Claims</span>
                    <span style={theme.adminStatValue}>{stats?.activeClaims || '---'}</span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Premium Rate</span>
                    <span style={theme.adminStatValue}>{adminData?.premiumRate || 3}%</span>
                  </div>
                  <div style={theme.adminStatCard}>
                    <span style={theme.adminStatLabel}>Free Claims</span>
                    <span style={{...theme.adminStatValue, color: canProtocolFundBond ? '#22c55e' : '#f59e0b'}}>
                      {canProtocolFundBond ? '✓ Enabled' : '✗ Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>⏸️ Contract Status</h3>
                <div style={theme.formGroup}>
                  <label style={theme.label}>Contract Status</label>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!isOwner) return;
                      setLoading(true);
                      setError(null);
                      setSuccess(null);
                      try {
                        const walletClient = getWalletClient();
                        const nextPaused = !isPaused;
                        const hash = await walletClient.writeContract({
                          address: config.insurance,
                          abi: ADMIN_ABI,
                          functionName: 'setPaused',
                          args: [nextPaused]
                        });
                        await publicClient.waitForTransactionReceipt({ hash });
                        setIsPaused(nextPaused);
                        setSuccess(`✅ Contract ${nextPaused ? 'paused' : 'unpaused'}.`);
                        loadProtocolStats();
                      } catch (err) {
                        setError('Failed: ' + (err.shortMessage || err.message));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    style={{
                      ...theme.adminBtn,
                      ...(isPaused ? theme.adminBtnGreen : theme.adminBtnRed),
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {isPaused ? '▶️ Unpause Contract' : '⏸️ Pause Contract'}
                  </button>
                  <span style={theme.hint}>
                    {isPaused
                      ? 'Contract is paused. No new purchases or deposits.'
                      : 'Contract is active.'}
                  </span>
                </div>
              </div>

              {/* Withdraw Fees */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>💵 Withdraw Protocol Fees</h3>
                <p style={theme.adminDesc}>
                  Available: <strong style={{color: '#22c55e'}}>${stats?.protocolFees || '0'}</strong> USDC
                </p>
                <button 
                  onClick={handleWithdrawFees}
                  disabled={loading || !stats || parseFloat(stats.protocolFees) === 0}
                  style={{
                    ...theme.adminBtn,
                    ...theme.adminBtnGreen,
                    opacity: (!stats || parseFloat(stats.protocolFees) === 0) ? 0.5 : 1
                  }}
                >
                  {loading ? '⏳ Processing...' : `Withdraw $${stats?.protocolFees || '0'} Fees`}
                </button>
              </div>

              {/* Withdraw from Pool */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>🏦 Withdraw from Pool</h3>
                <p style={theme.adminDesc}>
                  Pool Balance: <strong>${stats?.underwriterPool || '0'}</strong> USDC
                </p>
                <p style={theme.adminWarning}>
                  ⚠️ Withdrawing reduces available coverage. Use with caution.
                </p>
                <form onSubmit={handleWithdrawFromPool} style={theme.adminForm}>
                  <input
                    type="number"
                    value={withdrawPoolAmount}
                    onChange={(e) => setWithdrawPoolAmount(e.target.value)}
                    placeholder="Amount to withdraw"
                    style={theme.input}
                  />
                  <button 
                    type="submit"
                    disabled={loading || !withdrawPoolAmount}
                    style={{...theme.adminBtn, opacity: (!withdrawPoolAmount) ? 0.5 : 1}}
                  >
                    Withdraw from Pool
                  </button>
                </form>
              </div>

              {/* Set Premium Rate */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>📈 Premium Rate</h3>
                <p style={theme.adminDesc}>
                  Current: <strong>{adminData?.premiumRate || 3}%</strong> of coverage amount
                </p>
                <form onSubmit={handleSetPremiumRate} style={theme.adminForm}>
                  <div style={theme.inputWithSuffix}>
                    <input
                      type="number"
                      value={newPremiumRate}
                      onChange={(e) => setNewPremiumRate(e.target.value)}
                      placeholder="New rate (1-10)"
                      style={{...theme.input, flex: 1}}
                      min="1"
                      max="10"
                      step="0.5"
                    />
                    <span style={theme.inputSuffix}>%</span>
                  </div>
                  <span style={theme.hint}>Must be between 1% and 10%</span>
                  <button 
                    type="submit"
                    disabled={loading || !newPremiumRate}
                    style={{...theme.adminBtn, opacity: (!newPremiumRate) ? 0.5 : 1}}
                  >
                    Update Premium Rate
                  </button>
                </form>
              </div>

              {/* Lookup Policy */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>🔍 Lookup Policy</h3>
                <form onSubmit={handleLookupPolicy} style={theme.adminForm}>
                  <input
                    type="text"
                    value={lookupPolicyId}
                    onChange={(e) => setLookupPolicyId(e.target.value)}
                    placeholder="Enter Policy ID (0x...)"
                    style={theme.input}
                  />
                  <button 
                    type="submit"
                    disabled={loading || !lookupPolicyId}
                    style={{...theme.adminBtn, opacity: (!lookupPolicyId) ? 0.5 : 1}}
                  >
                    Lookup Policy
                  </button>
                </form>

                {lookupResult && (
                  <div style={theme.lookupResult}>
                    <div style={theme.lookupHeader}>
                      <span style={{
                        ...theme.statusBadge,
                        background: lookupResult.status === 'active' ? '#22c55e' : 
                                   lookupResult.status === 'expired' ? '#f59e0b' :
                                   lookupResult.status === 'paid_out' ? '#3b82f6' : '#ef4444'
                      }}>
                        {lookupResult.status.toUpperCase()}
                      </span>
                    </div>
                    <div style={theme.lookupGrid}>
                      <div>
                        <span style={theme.lookupLabel}>Policyholder</span>
                        <code style={theme.lookupValue}>{lookupResult.policyholder}</code>
                      </div>
                      <div>
                        <span style={theme.lookupLabel}>Coverage</span>
                        <span style={theme.lookupValue}>${lookupResult.coverageAmount}</span>
                      </div>
                      <div>
                        <span style={theme.lookupLabel}>Premium Paid</span>
                        <span style={theme.lookupValue}>${lookupResult.premiumPaid}</span>
                      </div>
                      <div>
                        <span style={theme.lookupLabel}>Expires</span>
                        <span style={theme.lookupValue}>{lookupResult.expirationTime}</span>
                      </div>
                      <div style={{gridColumn: '1 / -1'}}>
                        <span style={theme.lookupLabel}>Market Question</span>
                        <span style={theme.lookupValue}>{lookupResult.marketQuestion}</span>
                      </div>
                    </div>
                    {lookupResult.status === 'active' && (
                      <button 
                        onClick={() => setCancelPolicyId(lookupPolicyId)}
                        style={{...theme.adminBtn, ...theme.adminBtnRed, marginTop: '12px'}}
                      >
                        Cancel This Policy
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Cancel Policy (Emergency) */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>🚨 Cancel Policy (Emergency)</h3>
                <p style={theme.adminDesc}>
                  Cancel a policy and refund 80% of premium to policyholder.
                </p>
                <p style={theme.adminWarning}>
                  ⚠️ Only use for ambiguous markets, platform errors, or emergencies.
                </p>
                <form onSubmit={handleCancelPolicy} style={theme.adminForm}>
                  <input
                    type="text"
                    value={cancelPolicyId}
                    onChange={(e) => setCancelPolicyId(e.target.value)}
                    placeholder="Policy ID to cancel (0x...)"
                    style={theme.input}
                  />
                  <button 
                    type="submit"
                    disabled={loading || !cancelPolicyId}
                    style={{
                      ...theme.adminBtn,
                      ...theme.adminBtnRed,
                      opacity: (!cancelPolicyId) ? 0.5 : 1
                    }}
                  >
                    {loading ? '⏳ Processing...' : 'Cancel Policy'}
                  </button>
                </form>
              </div>

              {/* Contract Info */}
              <div style={theme.adminSection}>
                <h3 style={theme.adminSectionTitle}>📋 Contract Info</h3>
                <div style={theme.contractInfo}>
                  <div>
                    <span style={theme.infoLabel}>Coverage Contract</span>
                    <a 
                      href={`${config.explorer}/address/${config.insurance}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={theme.contractLink}
                    >
                      {config.insurance} ↗
                    </a>
                  </div>
                  <div>
                    <span style={theme.infoLabel}>USDC Token</span>
                    <a 
                      href={`${config.explorer}/address/${config.usdc}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={theme.contractLink}
                    >
                      {config.usdc} ↗
                    </a>
                  </div>
                  <div>
                    <span style={theme.infoLabel}>Network</span>
                    <span>{config.name}</span>
                  </div>
                  <div>
                    <span style={theme.infoLabel}>Minimum Bond</span>
                    <span>${adminData?.minimumBond || '750'} USDC</span>
                  </div>
                </div>
              </div>

              {/* Refresh Button */}
              <button 
                onClick={() => { loadProtocolStats(); loadAdminData(); }}
                style={theme.refreshDataBtn}
              >
                🔄 Refresh All Data
              </button>
            </div>
          )}
        </main>
      )}

      {/* Footer */}
      {legalDoc && (
        <div style={theme.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title">
          <div
            style={theme.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={theme.modalHeader}>
              <h2 id="legal-modal-title" style={theme.modalTitle}>
                {legalDocuments[legalDoc].title}
              </h2>
              <button
                type="button"
                onClick={closeLegalModal}
                style={theme.modalClose}
                aria-label="Close legal document"
              >
                ✕
              </button>
            </div>
            <div style={theme.modalBody}>
              <ReactMarkdown>{legalDocuments[legalDoc].content}</ReactMarkdown>
            </div>
          </div>
          <button
            type="button"
            onClick={closeLegalModal}
            style={theme.modalBackdrop}
            aria-label="Close legal document"
          />
        </div>
      )}
      <footer style={theme.footer}>
        <div style={theme.footerLinks}>
          <a href="https://docs.uma.xyz" target="_blank" rel="noopener noreferrer" style={theme.footerLink}>
            UMA Docs
          </a>
          <a href="https://polymarket.com" target="_blank" rel="noopener noreferrer" style={theme.footerLink}>
            Polymarket
          </a>
          {config.insurance !== '0x0000000000000000000000000000000000000000' && (
            <a href={`${config.explorer}/address/${config.insurance}`} target="_blank" rel="noopener noreferrer" style={theme.footerLink}>
              View Contract
            </a>
          )}
          <button
            type="button"
            style={theme.footerButton}
            onClick={() => setLegalDoc('coverage')}
          >
            Coverage Terms
          </button>
          <button
            type="button"
            style={theme.footerButton}
            onClick={() => setLegalDoc('terms')}
          >
            Terms of Service
          </button>
        </div>
        <p style={theme.footerText}>
          Built on UMA Optimistic Oracle V3 • Not financial advice • DYOR
        </p>
      </footer>
    </div>
  );
}


// ============ Theme Function ============

const getTheme = (darkMode) => {
  // Color palette
  const colors = darkMode ? {
    bg: '#0a0a0f',
    bgSecondary: 'rgba(255,255,255,0.02)',
    bgTertiary: 'rgba(255,255,255,0.03)',
    bgHover: 'rgba(255,255,255,0.05)',
    text: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.6)',
    textMuted: 'rgba(255,255,255,0.5)',
    textFaint: 'rgba(255,255,255,0.3)',
    border: 'rgba(255,255,255,0.06)',
    borderLight: 'rgba(255,255,255,0.1)',
    borderMedium: 'rgba(255,255,255,0.08)',
  } : {
    bg: '#f5f5f5',
    bgSecondary: 'rgba(0,0,0,0.02)',
    bgTertiary: 'rgba(0,0,0,0.04)',
    bgHover: 'rgba(0,0,0,0.06)',
    text: '#111111',
    textSecondary: 'rgba(0,0,0,0.6)',
    textMuted: 'rgba(0,0,0,0.5)',
    textFaint: 'rgba(0,0,0,0.3)',
    border: 'rgba(0,0,0,0.08)',
    borderLight: 'rgba(0,0,0,0.12)',
    borderMedium: 'rgba(0,0,0,0.1)',
  };

  const brand = {
    primary: '#ff5500',
    purple: '#6366f1',
    purpleLight: '#8b5cf6',
    green: '#22c55e',
    greenDark: '#16a34a',
    red: '#ef4444',
    orange: '#f59e0b',
    pink: '#ec4899',
  };

  return {
    container: {
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      position: 'relative',
      transition: 'background 0.3s ease, color 0.3s ease'
    },
    bgGradient: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: darkMode 
        ? 'radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.12) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(236, 72, 153, 0.08) 0%, transparent 50%)'
        : 'radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(255, 85, 0, 0.05) 0%, transparent 50%)',
      pointerEvents: 'none',
      zIndex: 0
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '14px 24px',
      position: 'relative',
      zIndex: 10,
      borderBottom: `1px solid ${colors.border}`,
      flexWrap: 'wrap',
      gap: '12px',
      background: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
      backdropFilter: 'blur(10px)'
    },
    logo: { display: 'flex', alignItems: 'center', gap: '8px' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    themeToggle: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    networkLabel: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '8px 12px',
      color: colors.text,
      fontSize: '13px'
    },
    accountArea: { display: 'flex', alignItems: 'center', gap: '8px' },
    balanceBadge: {
      background: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid rgba(34, 197, 94, 0.2)',
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '13px',
      color: brand.green
    },
    connectBtn: {
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.purpleLight})`,
      border: 'none',
      borderRadius: '8px',
      padding: '10px 18px',
      color: '#fff',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    accountBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '13px',
      fontFamily: 'monospace',
      cursor: 'pointer'
    },
    accountDot: { width: '8px', height: '8px', borderRadius: '50%', background: brand.green },
    alertError: {
      background: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '10px',
      padding: '12px 16px',
      margin: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      position: 'relative',
      zIndex: 10,
      fontSize: '13px'
    },
    alertSuccess: {
      background: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid rgba(34, 197, 94, 0.3)',
      borderRadius: '10px',
      padding: '12px 16px',
      margin: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      position: 'relative',
      zIndex: 10,
      fontSize: '13px'
    },
    alertClose: {
      marginLeft: 'auto',
      background: 'none',
      border: 'none',
      color: 'inherit',
      fontSize: '18px',
      cursor: 'pointer',
      opacity: 0.6
    },
    heroSection: { position: 'relative', zIndex: 10 },
    hero: { textAlign: 'center', padding: '60px 24px 50px' },
    heroTitle: { fontSize: '42px', fontWeight: '700', lineHeight: '1.1', margin: '0 0 16px' },
    heroAccent: {
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.pink}, ${brand.primary})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    heroSubtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
      maxWidth: '480px',
      margin: '0 auto 28px',
      lineHeight: '1.6'
    },
    heroBtn: {
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.purpleLight})`,
      border: 'none',
      borderRadius: '10px',
      padding: '14px 28px',
      color: '#fff',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    howItWorks: { padding: '50px 24px', background: colors.bgSecondary },
    sectionTitle: { textAlign: 'center', fontSize: '24px', fontWeight: '700', marginBottom: '36px' },
    stepsNote: {
      textAlign: 'center',
      fontSize: '12px',
      color: colors.textSecondary,
      margin: '-18px auto 28px',
      maxWidth: '520px',
      lineHeight: '1.5'
    },
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '20px',
      maxWidth: '800px',
      margin: '0 auto'
    },
    step: { textAlign: 'center', padding: '20px 12px' },
    stepNumber: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.purpleLight})`,
      fontSize: '16px',
      fontWeight: '700',
      marginBottom: '14px',
      color: '#fff'
    },
    stepTitle: { fontSize: '15px', fontWeight: '600', marginBottom: '6px' },
    stepDesc: { fontSize: '12px', color: colors.textSecondary, lineHeight: '1.5' },
    main: { maxWidth: '720px', margin: '0 auto', padding: '20px 24px 50px', position: 'relative', zIndex: 10 },
    tabs: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' },
    tab: {
      flex: '1 1 auto',
      minWidth: '100px',
      background: colors.bgTertiary,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '10px 12px',
      color: colors.textSecondary,
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      textAlign: 'center'
    },
    tabActive: {
      background: 'rgba(99, 102, 241, 0.1)',
      borderColor: 'rgba(99, 102, 241, 0.3)',
      color: colors.text
    },
    dashboard: { display: 'flex', flexDirection: 'column', gap: '20px' },
    dashboardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    dashboardTitle: { margin: 0, fontSize: '18px', fontWeight: '600' },
    reopenInfoBtn: {
      background: 'none',
      border: 'none',
      color: brand.purpleLight,
      fontSize: '12px',
      cursor: 'pointer',
      textDecoration: 'underline',
      padding: 0
    },
    infoBanner: {
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px'
    },
    infoBannerText: {
      margin: 0,
      fontSize: '12px',
      color: colors.textSecondary,
      lineHeight: '1.5'
    },
    infoBannerLink: {
      background: 'none',
      border: 'none',
      color: brand.purpleLight,
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      padding: 0
    },
    infoBannerDismiss: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '999px',
      color: colors.textSecondary,
      cursor: 'pointer',
      fontSize: '11px',
      padding: '6px 10px',
      whiteSpace: 'nowrap'
    },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' },
    statCard: {
      background: colors.bgTertiary,
      border: `1px solid ${colors.border}`,
      borderRadius: '10px',
      padding: '14px',
      textAlign: 'center'
    },
    statValue: { display: 'block', fontSize: '20px', fontWeight: '700', marginBottom: '4px' },
    statLabel: { fontSize: '10px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' },
    section: {
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '14px',
      padding: '18px'
    },
    underwriteStack: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' },
    underwriteParagraph: { margin: 0, fontSize: '13px', color: colors.textSecondary, lineHeight: '1.6' },
    underwriteList: { margin: '8px 0 0', paddingLeft: '18px', lineHeight: '1.7', fontSize: '13px' },
    tableWrapper: { overflowX: 'auto', marginTop: '8px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    tableHeader: {
      textAlign: 'left',
      padding: '8px 10px',
      borderBottom: `1px solid ${colors.borderLight}`,
      color: colors.textSecondary,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },
    tableCell: {
      padding: '10px',
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text
    },
    sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
    sectionTitle2: { fontSize: '16px', fontWeight: '600', margin: 0 },
    refreshBtn: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '6px',
      padding: '6px 10px',
      color: colors.textSecondary,
      fontSize: '11px',
      cursor: 'pointer'
    },
    emptyState: { textAlign: 'center', padding: '36px 16px' },
    emptyIcon: { fontSize: '40px', display: 'block', marginBottom: '10px', opacity: 0.4 },
    emptyText: { fontSize: '14px', marginBottom: '4px' },
    emptySubtext: { fontSize: '12px', color: colors.textMuted, marginBottom: '16px' },
    emptyBtn: {
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.purpleLight})`,
      border: 'none',
      borderRadius: '8px',
      padding: '10px 18px',
      color: '#fff',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    policiesList: { display: 'flex', flexDirection: 'column', gap: '10px' },
    policyCard: {
      background: colors.bgTertiary,
      border: `1px solid ${colors.borderMedium}`,
      borderRadius: '10px',
      padding: '14px'
    },
    policyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    statusBadge: { padding: '3px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' },
    policyId: { fontSize: '11px', fontFamily: 'monospace', color: colors.textMuted },
    policyBody: {},
    policyQuestion: { fontSize: '13px', fontWeight: '500', marginBottom: '10px', lineHeight: '1.4' },
    policyStats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
    policyStatLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, marginBottom: '2px' },
    policyStatValue: { fontSize: '13px', fontWeight: '600' },
    policyActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: `1px solid ${colors.border}`
    },
    claimBtn: {
      background: `linear-gradient(135deg, ${brand.orange}, ${brand.red})`,
      border: 'none',
      borderRadius: '6px',
      padding: '7px 14px',
      color: '#fff',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    viewTxLink: { color: colors.textMuted, textDecoration: 'none', fontSize: '11px', padding: '7px 10px' },
    tabContent: {
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '14px',
      padding: '20px'
    },
    formTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '18px' },
    form: { display: 'flex', flexDirection: 'column', gap: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
    label: { fontSize: '12px', fontWeight: '500', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)' },
    labelSmall: { fontSize: '11px', fontWeight: '500', color: colors.textSecondary },
    input: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '11px 13px',
      color: colors.text,
      fontSize: '14px'
    },
    hint: { fontSize: '11px', color: colors.textMuted, lineHeight: '1.4' },
    hintWarning: { fontSize: '11px', color: brand.orange, lineHeight: '1.4' },
    hintSuccess: { fontSize: '11px', color: brand.green, lineHeight: '1.4' },
    searchResults: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginTop: '6px'
    },
    marketGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '8px',
      marginTop: '6px'
    },
    searchResultBtn: {
      background: colors.bgTertiary,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '10px 12px',
      textAlign: 'left',
      cursor: 'pointer',
      color: colors.text
    },
    searchResultQuestion: { fontSize: '13px', fontWeight: '600', marginBottom: '4px' },
    searchResultMeta: { fontSize: '11px', color: colors.textMuted, display: 'flex', gap: '10px', flexWrap: 'wrap' },
    ownedBadge: {
      background: darkMode ? 'rgba(34, 197, 94, 0.18)' : 'rgba(34, 197, 94, 0.12)',
      color: brand.green,
      padding: '2px 6px',
      borderRadius: '999px',
      fontSize: '10px',
      fontWeight: '600'
    },
    premiumBox: {
      background: colors.bgTertiary,
      border: `1px solid ${colors.border}`,
      borderRadius: '10px',
      padding: '12px'
    },
    premiumRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `1px solid ${colors.border}`
    },
    textarea: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '11px 13px',
      color: colors.text,
      fontSize: '14px',
      resize: 'vertical',
      minHeight: '80px',
      fontFamily: 'inherit'
    },
    select: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      padding: '11px 13px',
      color: colors.text,
      fontSize: '14px',
      cursor: 'pointer'
    },
    premiumDisplay: {
      background: 'rgba(99, 102, 241, 0.1)',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      borderRadius: '10px',
      padding: '14px',
      textAlign: 'center'
    },
    premiumLabel: { display: 'block', fontSize: '12px', color: colors.textMuted, marginBottom: '6px' },
    premiumValue: { fontSize: '24px', fontWeight: '700', color: brand.purple },
    premiumNote: { fontSize: '11px', color: colors.textMuted, marginTop: '6px' },
    submitBtn: {
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.purpleLight})`,
      border: 'none',
      borderRadius: '10px',
      padding: '14px',
      color: '#fff',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' },
    checkbox: { width: '15px', height: '15px', cursor: 'pointer' },
    evidenceGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    warningBox: {
      background: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '12px',
      lineHeight: '1.5'
    },
    disclaimerBox: {
      background: 'rgba(59, 130, 246, 0.08)',
      border: '1px solid rgba(59, 130, 246, 0.25)',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '11px',
      lineHeight: '1.5',
      color: colors.textSecondary
    },
    infoBox: {
      background: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid rgba(34, 197, 94, 0.2)',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '6px'
    },
    infoTitle: { margin: '0 0 8px', fontSize: '15px' },
    infoList: { margin: '8px 0 0', paddingLeft: '16px', lineHeight: '1.7', fontSize: '13px' },
    infoRisk: { marginTop: '10px', color: colors.textSecondary, fontSize: '12px' },
    footer: {
      textAlign: 'center',
      padding: '28px',
      borderTop: `1px solid ${colors.border}`,
      position: 'relative',
      zIndex: 10
    },
    footerLinks: { display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' },
    footerLink: { color: colors.textMuted, textDecoration: 'none', fontSize: '12px' },
    footerButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      color: colors.textMuted,
      fontSize: '12px',
      cursor: 'pointer',
      textDecoration: 'underline',
      textUnderlineOffset: '3px'
    },
    footerText: { fontSize: '10px', color: colors.textFaint },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      zIndex: 100
    },
    modalBackdrop: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      zIndex: 100
    },
    modalCard: {
      position: 'relative',
      zIndex: 101,
      width: 'min(860px, 100%)',
      maxHeight: '85vh',
      overflow: 'hidden',
      background: darkMode ? 'rgba(15, 17, 26, 0.98)' : '#ffffff',
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      display: 'flex',
      flexDirection: 'column'
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '18px 20px',
      borderBottom: `1px solid ${colors.border}`
    },
    modalTitle: { margin: 0, fontSize: '18px', fontWeight: '600' },
    modalClose: {
      background: colors.bgHover,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      color: colors.text,
      cursor: 'pointer'
    },
    modalBody: {
      padding: '18px 20px 24px',
      overflowY: 'auto',
      lineHeight: 1.6,
      fontSize: '13px',
      color: colors.textSecondary
    },
    adminTab: { background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' },
    adminHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
    ownerBadge: { background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', fontFamily: 'monospace', color: brand.red },
    adminSection: { background: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '16px', marginBottom: '14px' },
    adminSectionTitle: { fontSize: '14px', fontWeight: '600', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '6px' },
    adminStatsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' },
    adminStatCard: { background: colors.bgTertiary, borderRadius: '8px', padding: '12px', textAlign: 'center' },
    adminStatLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' },
    adminStatValue: { fontSize: '18px', fontWeight: '700' },
    adminDesc: { fontSize: '13px', color: colors.textSecondary, marginBottom: '10px' },
    adminWarning: { fontSize: '12px', color: brand.orange, marginBottom: '10px' },
    adminForm: { display: 'flex', flexDirection: 'column', gap: '10px' },
    adminBtn: { background: 'rgba(99, 102, 241, 0.2)', border: '1px solid rgba(99, 102, 241, 0.4)', borderRadius: '8px', padding: '10px 16px', color: colors.text, fontSize: '13px', fontWeight: '500', cursor: 'pointer', alignSelf: 'flex-start' },
    adminBtnGreen: { background: 'rgba(34, 197, 94, 0.2)', borderColor: 'rgba(34, 197, 94, 0.4)' },
    adminBtnRed: { background: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.4)' },
    inputWithSuffix: { display: 'flex', alignItems: 'center', gap: '8px' },
    inputSuffix: { color: colors.textMuted, fontSize: '14px' },
    lookupResult: { marginTop: '14px', padding: '14px', background: colors.bgTertiary, borderRadius: '8px', border: `1px solid ${colors.borderMedium}` },
    lookupHeader: { marginBottom: '10px' },
    lookupGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' },
    lookupLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, marginBottom: '2px' },
    lookupValue: { fontSize: '12px', wordBreak: 'break-all' },
    contractInfo: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' },
    infoLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, marginBottom: '3px' },
    contractLink: { fontSize: '11px', fontFamily: 'monospace', color: brand.purple, wordBreak: 'break-all', textDecoration: 'none' },
    inlineLink: { color: brand.purpleLight, textDecoration: 'underline' },
    refreshDataBtn: { background: colors.bgHover, border: `1px solid ${colors.borderLight}`, borderRadius: '8px', padding: '10px 16px', color: colors.textSecondary, fontSize: '12px', cursor: 'pointer', marginTop: '6px' },
    lpPositionBox: { background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(99, 102, 241, 0.1))', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '12px', padding: '16px', marginBottom: '16px' },
    lpPositionTitle: { fontSize: '14px', fontWeight: '600', margin: '0 0 12px' },
    lpStatsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '14px' },
    lpStatItem: { textAlign: 'center' },
    lpStatLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '3px' },
    lpStatValue: { fontSize: '16px', fontWeight: '700' },
    withdrawSection: { borderTop: `1px solid ${colors.borderLight}`, paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
    withdrawBtn: { background: `linear-gradient(135deg, ${brand.green}, ${brand.greenDark})`, border: 'none', borderRadius: '8px', padding: '12px 20px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    cooldownHint: { fontSize: '11px', color: colors.textMuted, textAlign: 'center' },
    poolStatsBox: { background: colors.bgTertiary, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '14px' },
    poolStatsTitle: { fontSize: '13px', fontWeight: '600', margin: '0 0 10px' },
    poolStatsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
    poolStatLabel: { display: 'block', fontSize: '10px', color: colors.textMuted, marginBottom: '2px' },
    poolStatValue: { fontSize: '14px', fontWeight: '600' },
    progressContainer: {
      background: colors.bgTertiary,
      border: `1px solid ${colors.border}`,
      borderRadius: '10px',
      padding: '12px'
    },
    progressHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    progressLabel: { fontSize: '12px', fontWeight: '600' },
    progressSteps: { fontSize: '11px', color: colors.textSecondary },
    progressTrack: {
      width: '100%',
      height: '8px',
      background: colors.bgHover,
      borderRadius: '999px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      background: `linear-gradient(135deg, ${brand.purple}, ${brand.pink})`,
      borderRadius: '999px',
      transition: 'width 0.2s ease'
    }
  };
};
